/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/App.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed                     |
 * |---|----------------------|-------------------|------------------------|----------------------------------|
 * | 1 | 2026-06-15 (Step 2)  | Claude Sonnet 4.6 | Studio SPA setup       | Created — Phase 0 placeholder    |
 * | 2 | 2026-06-15 (Step 3)  | Claude Sonnet 4.6 | DeviceSession (Step 3) | Connect button + DeviceSession   |
 * | 3 | 2026-06-15 (Step 4)  | Claude Sonnet 4.6 | scrcpy mirror (Step 4) | Canvas + ScrcpyMirror hook       |
 * | 4 | 2026-06-15 (Step 5)  | Claude Sonnet 4.6 | dumpTree (Step 5)      | Tree dump button + node count    |
 * | 5 | 2026-06-16 (Step 6)  | Claude Sonnet 4.6 | Recorder v0 (Step 6)   | RecorderV0 hook + steps list     |
 * | 6 | 2026-06-16 (Step 7)  | Claude Sonnet 4.6 | Replay v0 (Step 7)     | replaySteps hook + replay panel  |
 * | 7 | 2026-06-16 (layout)  | Claude Sonnet 4.6 | side-by-side layout    | mirror left + recorder/replay right when streaming |
 * | 8 | 2026-06-16 (bugfix)  | Claude Sonnet 4.6 | black canvas fix        | aspectRatio inline style on canvas from mirrorState.width/height |
 * | 9 | 2026-06-16 (bugfix2) | Claude Sonnet 4.6 | black canvas root cause  | DeviceMirror was unmounted/remounted when mirrorStreaming toggled because it moved between DOM positions (direct child vs child-inside-studio-layout). Fix: always wrap in same div; only side panel conditionally renders. Canvas DOM element is now stable. |
 * | 10| 2026-06-16 (bugfix3) | Claude Sonnet 4.6 | frame counter display    | Show framesRendered count in mirror header so user can confirm rendering is live without opening DevTools |
 * | 11| 2026-06-16 (cmds)    | Claude Sonnet 4.6 | Custom commands          | CommandToolbar (Open App, Close App, Home, Back, Enter, Wait, Screenshot, Type Text); addStep in useRecorder; stepDelayMs config in ReplayPanel; stepDescription() for non-tap steps; error status in results |
 * | 12| 2026-06-16 (editing) | Claude Sonnet 4.6 | Step editing + replay reliability | Candidate inspector (expandable per-step); strategy confidence colors; delete/re-record per step; matchedBy in replay results; relative resolver; className removed from recorder |
 * | 13| 2026-06-16 (reorder) | Claude Sonnet 4.6 | Step reorder + edit non-tap   | ↑/↓ move buttons on all steps; edit form for non-tap steps (launchApp/stopApp/wait/typeText/pressKey/screenshot); moveStep + updateStep in useRecorder |
 * | 14| 2026-06-17 (export) | Claude Sonnet 4.6 | Export IR button (Phase 1 bridge) | "Export IR" button in RecorderPanel header downloads recorded steps as valid TestIR JSON file |
 * | 15| 2026-06-17 (live-replay) | Claude Sonnet 4.6 | Live replay progress | ReplayPanel shows running log: completed steps get ok/fail badge, current step shows "running" with spinner. useReplayer now tracks executingIndex and accumulates results via onStepStart/onStepResult callbacks. |
 * | 16| 2026-06-17 (click-overlay) | Claude Sonnet 4.6 | Click indicator overlay | DeviceMirror shows a ripple animation at each tap point. Settings (enabled toggle + color picker) live inside DeviceMirror state — no lift needed. |
 * | 17| 2026-06-17 (heal-hint)    | Claude Sonnet 4.6 | Phase 2 heal bridge  | ReplayPanel shows replay-heal-hint on not_found steps that have a fingerprint, pointing user to CLI --heal workflow |
 * | 18| 2026-06-18 (phase3)       | Claude Sonnet 4.6 | Phase 3 integration  | 3-panel layout (TestLibrary|mirror|editor); SaveTestModal; ExecutionHistory; MonacoView; dnd-kit drag-drop; save-run-to-Dexie after replay; device info extraction |
 * | 19| 2026-06-18 (phase3b)      | Claude Sonnet 4.6 | 4-tab sidebar nav    | Sidebar (4 tabs); TestCasesTab; ExecutionTab (Play/Pause/Stop); ReportsTab; canvas always mounted (CSS visibility); runQueue state; Record tab replay no longer saves to Dexie |
 * | 20| 2026-06-18 (phase3c)      | Claude Sonnet 4.6 | Inspect Mode         | InspectorV0 + useInspector hook; canvasMode Drive/Inspect toggle + Alt-hold; InspectPanel (attributes + assertion form); inspect highlight rect on canvas overlay |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/main.tsx  (default import)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - App (default export): top-level React component.
 *   Phase 0: capability checks + Connect + scrcpy mirror canvas +
 *            tree dump + Recorder (record/stop/clear + command toolbar) + Replay.
 *
 * CALLER → CALLEE MAP
 * --------------------
 * main.tsx → <App />
 *   → useDeviceSession()    → DeviceSession.connect() / disconnect()
 *   → useScrcpyMirror()     → ScrcpyMirror.start() / stop()
 *   → useDumpTree()         → dumpTree(adb)
 *   → useRecorder()         → RecorderV0.start() / stop() / clearSteps() / addStep() / deleteStep() / reRecordStep() / reorderCandidateAt() / removeCandidateAt()
 *   → useReplayer()         → replaySteps(steps, adb, { stepDelayMs })
 *   → <DeviceMirror />      → canvas + mirror controls
 *   → <RecorderPanel />     → Record/Stop/Clear + CommandToolbar + step list + candidate inspector
 *   → <ReplayPanel />       → Replay button + stepDelayMs config + per-step results with matchedBy
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. useDeviceSession creates ONE DeviceSession per App mount via useRef.
 * 2. useScrcpyMirror creates ONE ScrcpyMirror per App mount.
 * 3. The canvas ref is stable — React must never recreate the DOM element
 *    across re-renders. DeviceMirror is always kept at position 0 inside
 *    the same wrapper div regardless of streaming state. Changing its
 *    position in the JSX tree causes React to unmount/remount the canvas —
 *    ScrcpyMirror then draws to the detached old canvas and the visible canvas is black.
 * 4. CommandToolbar is always visible when streaming. It adds steps directly
 *    to the step list (without requiring canvas taps). For commands needing a
 *    parameter (package name, duration, text) a small inline form appears.
 * 5. stepDelayMs is configurable in the ReplayPanel. Default: 800ms.
 * 6. Each tap step in the step list is expandable to show its candidate list.
 *    Candidates can be reordered (↑ promotes to position 0) or deleted.
 *    The ↺ Re-record button uses RecorderV0.setReplaceMode() to overwrite only
 *    that step instead of appending a new one.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - ScrcpyMirror.start() is called inside a useEffect that watches adb state.
 *   We track whether start has been called with a ref to avoid calling it twice
 *   on React StrictMode double-invocation.
 * - strategyConfidence("className") returns "low" but className is no longer
 *   emitted by buildSelector — it appears only in manually-added candidates.
 *
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState, useId } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DeviceSession } from "./device/DeviceSession.js";
import type { DeviceState, ConnectionError } from "./device/DeviceSession.js";
import { ScrcpyMirror } from "./device/ScrcpyMirror.js";
import type { MirrorState } from "./device/ScrcpyMirror.js";
import { dumpTree } from "./device/treeParser.js";
import type { A11yNode } from "./device/treeParser.js";
import { RecorderV0 } from "./device/recorder.js";
import { replaySteps } from "./device/replayer.js";
import type { ReplayStepResult, ScriptResult } from "./device/replayer.js";
import type { IRStep, TapStep, PressKeyName, TestIR, AssertionKind, Selector, SetVariableStep } from "@mimiq/ir";
import { generateId, IR_VERSION, validateIR } from "@mimiq/ir";
import { LocatorField, LOCATOR_STRATEGIES } from "./components/LocatorField.js";
import type { LocatorStrategy } from "./components/LocatorField.js";
import type { Adb } from "@yume-chan/adb";
import { Sidebar } from "./components/Sidebar.js";
import type { StudioTab } from "./components/Sidebar.js";
import { InspectorV0 } from "./device/inspector.js";
import type { InspectResult } from "./device/inspector.js";
import { InspectPanel } from "./components/InspectPanel.js";
import { TestCasesTab } from "./components/TestCasesTab.js";
import { ExecutionTab } from "./components/ExecutionTab.js";
import { ReportsTab } from "./components/ReportsTab.js";
import { SettingsPage, getTheme, applyTheme, getAiAssistEnabled } from "./components/SettingsPage.js";
import { AssistBar } from "./components/AssistBar.js";
import { WikiPage } from "./components/WikiPage.js";
import { RecordLandingPage } from "./components/RecordLandingPage.js";
import { FunctionsTab } from "./components/FunctionsTab.js";
import { db as studioDb } from "./db/index.js";
import { useLiveQuery } from "dexie-react-hooks";

// Apply persisted theme immediately before first render
applyTheme(getTheme());
import { extractJsonsFromZip } from "./utils/exportUtils.js";
import { QuickAddStep } from "./components/QuickAddStep.js";
import { MonacoView } from "./components/MonacoView.js";
import { SaveTestModal } from "./components/SaveTestModal.js";
import type { TestCase } from "./db/schema.js";
import "./App.css";

/* ── Script sandbox (iframe postMessage) ──────────────────────────────────── */

// Inline HTML for the hidden sandbox iframe.
// allow-scripts only — no same-origin, no forms, no popups.
const SANDBOX_SRCDOC = `<!DOCTYPE html><html><body><script>
window.addEventListener('message',async function(e){
  var d=e.data;
  if(!d||d.type!=='mimiq-script')return;
  try{
    var fn=new Function('context',d.code);
    var ctx={vars:JSON.parse(JSON.stringify(d.vars||{}))};
    var result=await Promise.resolve(fn(ctx));
    e.source.postMessage({id:d.id,ok:true,result:result||{}},e.origin||'*');
  }catch(err){
    e.source.postMessage({id:d.id,ok:false,error:String(err)},e.origin||'*');
  }
});<\/script></body></html>`;

function useScriptSandbox() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pending   = useRef(new Map<string, { resolve: (r: ScriptResult) => void; timer: ReturnType<typeof setTimeout> }>());
  const counter   = useRef(0);

  useEffect(() => {
    function onMsg(e: MessageEvent) {
      const d = e.data as { id?: string; ok?: boolean; result?: ScriptResult; error?: string };
      if (!d?.id) return;
      const p = pending.current.get(d.id);
      if (!p) return;
      clearTimeout(p.timer);
      pending.current.delete(d.id);
      if (d.ok) p.resolve((d.result ?? {}) as ScriptResult);
      else p.resolve({ fail: d.error ?? "Script error" });
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const scriptRunner = useCallback((code: string, vars: Record<string, unknown>): Promise<ScriptResult> =>
    new Promise((resolve) => {
      const id = String(++counter.current);
      const timer = setTimeout(() => {
        pending.current.delete(id);
        resolve({ fail: "Script timed out after 5 seconds" });
      }, 5000);
      pending.current.set(id, { resolve, timer });
      iframeRef.current?.contentWindow?.postMessage({ type: "mimiq-script", id, code, vars }, "*");
    }), []);

  return { iframeRef, scriptRunner };
}

/* ── Shared flow loader for replayer ──────────────────────────────────────── */

async function loadSharedFlow(id: string) {
  const sf = await studioDb.sharedFlows.get(id);
  if (!sf) return undefined;
  return { name: sf.name, steps: sf.steps, parameters: sf.parameters };
}

/* ── Runtime capability checks ────────────────────────────────────────────── */

const hasWebUsb = "usb" in navigator;
const hasWebCodecs = "VideoDecoder" in window;
const isCrossOriginIsolated = window.crossOriginIsolated ?? false;

/* ── Step display helpers ─────────────────────────────────────────────────── */

export function swipeDirectionLabel(from: [number, number], to: [number, number]): string {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  if (Math.abs(dy) >= Math.abs(dx)) return dy < 0 ? "Swipe Up" : "Swipe Down";
  return dx < 0 ? "Swipe Left" : "Swipe Right";
}

export function stepDescription(step: IRStep): string {
  switch (step.type) {
    case "tap":               return step.selector.description;
    case "doubleTap":         return step.selector.description;
    case "longPress":         return `${step.selector.description} (${step.durationMs ?? 800}ms)`;
    case "launchApp":         return step.packageName;
    case "stopApp":           return step.packageName;
    case "pressKey":          return step.key;
    case "wait":              return `${step.durationMs}ms`;
    case "screenshot":        return step.label ?? "(screenshot)";
    case "typeText":          return step.sensitive ? `🔒 "••••••••"` : `"${step.text}"`;
    case "clearText":         return step.selector.description;
    case "hideKeyboard":      return "hide keyboard";
    case "swipe":             return swipeDirectionLabel(step.from, step.to);
    case "holdSwipe":         return `Hold+${step.direction ?? "down"} on "${step.selector.description}"`;
    case "evaluate":          return `${step.left} ${step.operator} ${step.right ?? ""}${step.storeAs ? ` → ${step.storeAs}` : ""}`;
    case "script":            return step.code.split("\n").find(l => l.trim() && !l.trim().startsWith("//"))?.trim().slice(0, 60) ?? "(script)";
    case "callFlow":          return step.sharedFlowName ? `♻ ${step.sharedFlowName}` : `flow ${step.flowId ?? "(unknown)"}`;
    case "scrollUntilVisible":return `scroll ${step.direction ?? "down"} → ${step.selector.description}`;
    case "waitFor":           return `wait ${step.state ?? "visible"}: ${step.selector.description}`;
    case "openUrl":           return step.url;
    case "deepLink":          return step.url;
    case "api":               return `${step.method} ${step.url}`;
    case "script":            return "(script)";
    case "setDeviceState": {
      const parts: string[] = [];
      if (step.wifi !== undefined)      parts.push(`wifi=${step.wifi ? "on" : "off"}`);
      if (step.airplane !== undefined)  parts.push(`airplane=${step.airplane ? "on" : "off"}`);
      if (step.darkMode !== undefined)  parts.push(`darkMode=${step.darkMode ? "on" : "off"}`);
      if (step.location !== undefined)  parts.push(`location=${step.location ? "on" : "off"}`);
      if (step.showKeyboard)            parts.push("showKeyboard");
      return parts.join(", ") || "device state";
    }
    case "assert": {
      const desc = step.selector.description;
      return step.value ? `${desc} ${step.assertion} "${step.value}"` : `${desc} ${step.assertion}`;
    }
    case "if":          return `if ${step.condition.assertion}`;
    case "repeat":      return `repeat ×${step.count}`;
    case "forEachRow":  return `for each ${step.variable}`;
    case "print":       return `[${step.level ?? "info"}] ${step.message}`;
    case "setVariable":
      if (step.source.kind === "literal")   return `${step.name} = "${step.source.value}"`;
      if (step.source.kind === "random")    return `${step.name} ← random ${step.source.randomType}(${step.source.randomType !== "number" ? `${step.source.length ?? 8} chars` : `${step.source.min ?? 0}–${step.source.max ?? 999999}`})`;
      if (step.source.kind === "attribute") return `${step.name} ← ${step.source.selector.description}.${step.source.attribute}`;
      if (step.source.kind === "count")     return `${step.name} ← count(${step.source.selector.description})`;
      return `${step.name} ← ${step.source.selector.description}`;
    case "checkpoint":  return `${step.status === "pass" ? "✓ PASS" : "✗ FAIL"}: ${step.message}`;
    case "stopTest":    return `stop (${step.status ?? "failed"}): ${step.message ?? ""}`;
    case "break":       return "exit loop";
    case "continue":    return "next iteration";
    default: return (step as { type: string }).type;
  }
}

function stepFirstStrategy(step: IRStep): string {
  if (step.type === "tap") {
    return step.selector.candidates[0]?.strategy ?? "coord";
  }
  return "—";
}

function strategyConfidence(strategy: string): "high" | "medium" | "low" | "none" {
  if (strategy === "a11yId" || strategy === "text") return "high";
  if (strategy === "contentDesc" || strategy === "relative") return "medium";
  if (strategy === "className") return "low";
  return "none"; // coord, unknown, platform-specific
}

/* ── useDeviceSession ─────────────────────────────────────────────────────── */

function useDeviceSession() {
  const sessionRef = useRef<DeviceSession | null>(null);
  const [state, setState] = useState<DeviceState>({ status: "idle" });

  useEffect(() => {
    const session = new DeviceSession();
    sessionRef.current = session;
    const unsubscribe = session.subscribe(setState);
    return () => {
      unsubscribe();
      session.disconnect().catch(() => {});
    };
  }, []);

  const connect = useCallback(() => sessionRef.current?.connect(), []);
  const disconnect = useCallback(() => sessionRef.current?.disconnect(), []);

  return { state, connect, disconnect };
}

/* ── useScrcpyMirror ──────────────────────────────────────────────────────── */

function useScrcpyMirror(adb: Adb | null, canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  const mirrorRef = useRef<ScrcpyMirror | null>(null);
  const [mirrorState, setMirrorState] = useState<MirrorState>({ status: "idle" });
  const startedRef = useRef(false);

  useEffect(() => {
    const mirror = new ScrcpyMirror();
    mirrorRef.current = mirror;
    const unsub = mirror.subscribe(setMirrorState);
    return () => {
      unsub();
      mirror.stop().catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (!adb || !canvasRef.current) return;
    if (startedRef.current) return;
    startedRef.current = true;

    mirrorRef.current?.start(adb, canvasRef.current).catch(() => {});

    return () => {
      startedRef.current = false;
      mirrorRef.current?.stop().catch(() => {});
    };
  }, [adb, canvasRef]);

  const stopMirror = useCallback(() => mirrorRef.current?.stop(), []);

  const restartMirror = useCallback(() => {
    if (!adb || !canvasRef.current) return;
    startedRef.current = true;
    mirrorRef.current?.start(adb, canvasRef.current).catch(() => {});
  }, [adb, canvasRef]);

  return { mirrorState, stopMirror, restartMirror };
}

/* ── useDumpTree ──────────────────────────────────────────────────────────── */

function useDumpTree() {
  const [nodes, setNodes] = useState<A11yNode[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dump = useCallback(async (adb: Adb) => {
    setLoading(true);
    setError(null);
    try {
      const result = await dumpTree(adb);
      setNodes(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { nodes, loading, error, dump };
}

/* ── useInspector ─────────────────────────────────────────────────────────── */

type CanvasMode = "drive" | "inspect" | "assist";

function useInspector() {
  const inspectorRef = useRef<InspectorV0 | null>(null);
  const [inspectResult, setInspectResult] = useState<InspectResult | null>(null);
  const [inspecting, setInspecting] = useState(false); // true while dumpTree is in-flight

  useEffect(() => {
    const inspector = new InspectorV0({
      onDumpStart: () => setInspecting(true),
      onDumpEnd:   () => setInspecting(false),
    });
    inspectorRef.current = inspector;
    const unsub = inspector.subscribe(setInspectResult);
    return () => { unsub(); inspector.stop(); };
  }, []);

  const startInspecting = useCallback((adb: Adb, canvas: HTMLCanvasElement) => {
    inspectorRef.current?.start(adb, canvas);
    setInspectResult(null);
  }, []);

  const stopInspecting = useCallback(() => {
    inspectorRef.current?.stop();
    setInspectResult(null);
    setInspecting(false);
  }, []);

  return { inspectorRef, inspectResult, inspecting, startInspecting, stopInspecting };
}

/* ── useRecorder ──────────────────────────────────────────────────────────── */

function useRecorder(
  adb: Adb | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
) {
  const recorderRef = useRef<RecorderV0 | null>(null);
  const [steps, setSteps] = useState<readonly IRStep[]>([]);
  const [recording, setRecording] = useState(false);
  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);

  useEffect(() => {
    const recorder = new RecorderV0();
    recorderRef.current = recorder;
    const unsub = recorder.subscribe(setSteps);
    return () => {
      unsub();
      recorder.stop();
    };
  }, []);

  const startRecording = useCallback(() => {
    if (!adb || !canvasRef.current) return;
    recorderRef.current?.start(adb, canvasRef.current);
    setRecording(true);
  }, [adb, canvasRef]);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
    setRecording(false);
    setReplacingIndex(null);
  }, []);

  const clearSteps = useCallback(() => {
    recorderRef.current?.clearSteps();
  }, []);

  const addStep = useCallback((step: IRStep) => {
    recorderRef.current?.addStep(step);
  }, []);

  const deleteStep = useCallback((i: number) => {
    recorderRef.current?.deleteStep(i);
  }, []);

  const reRecordStep = useCallback((i: number) => {
    if (!adb || !canvasRef.current) return;
    recorderRef.current?.stop(); // detach any active recording first
    recorderRef.current?.setReplaceMode(i);
    recorderRef.current?.start(adb, canvasRef.current, {
      onAutoStop: () => {
        setRecording(false);
        setReplacingIndex(null);
      },
    });
    setRecording(true);
    setReplacingIndex(i);
  }, [adb, canvasRef]);

  const reorderCandidateAt = useCallback((si: number, ci: number) => {
    recorderRef.current?.reorderCandidate(si, ci);
  }, []);

  const removeCandidateAt = useCallback((si: number, ci: number) => {
    recorderRef.current?.removeCandidate(si, ci);
  }, []);

  const moveStep = useCallback((i: number, direction: "up" | "down") => {
    recorderRef.current?.moveStep(i, direction);
  }, []);

  const updateStep = useCallback((i: number, step: IRStep) => {
    recorderRef.current?.updateStep(i, step);
  }, []);

  const updateCandidateAt = useCallback(
    (si: number, ci: number, updates: Partial<Pick<import("@mimiq/ir").Candidate, "strategy" | "value" | "index">>) => {
      recorderRef.current?.updateCandidate(si, ci, updates);
    }, [],
  );

  const addCandidateAt = useCallback(
    (si: number, candidate: import("@mimiq/ir").Candidate) => {
      recorderRef.current?.addCandidate(si, candidate);
    }, [],
  );

  const reorderStepsByIds = useCallback((orderedIds: string[]) => {
    recorderRef.current?.reorderStepsByIds(orderedIds);
  }, []);

  const loadSteps = useCallback((newSteps: readonly IRStep[]) => {
    recorderRef.current?.clearSteps();
    for (const step of newSteps) {
      recorderRef.current?.addStep(step);
    }
  }, []);

  const addNestedStep = useCallback((parentId: string, branch: "then" | "else" | "steps", step: IRStep) => {
    recorderRef.current?.addNestedStep(parentId, branch, step);
  }, []);

  const deleteNestedStep = useCallback((parentId: string, branch: "then" | "else" | "steps", index: number) => {
    recorderRef.current?.deleteNestedStep(parentId, branch, index);
  }, []);

  const moveNestedStep = useCallback((parentId: string, branch: "then" | "else" | "steps", index: number, direction: "up" | "down") => {
    recorderRef.current?.moveNestedStep(parentId, branch, index, direction);
  }, []);

  const updateNestedStep = useCallback((parentId: string, branch: "then" | "else" | "steps", index: number, step: IRStep) => {
    recorderRef.current?.updateNestedStep(parentId, branch, index, step);
  }, []);

  const startCapture = useCallback((callback: (sel: Selector) => void) => {
    if (!adb || !canvasRef.current) return;
    const recorder = recorderRef.current;
    if (!recorder) return;

    // If the recorder is not active (not currently recording), temporarily
    // start its canvas listeners so the next tap on the mirror is captured.
    // We do NOT set recording=true — the red recording indicator stays off.
    const wasActive = recorder.isActive;
    if (!wasActive) {
      recorder.start(adb, canvasRef.current);
    }

    recorder.setCaptureMode((sel) => {
      // After capture, detach the temporary listeners (if we started them)
      if (!wasActive) {
        recorder.stop();
      }
      callback(sel);
    });
  }, [adb, canvasRef]);

  const stopCapture = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorder.clearCaptureMode();
    // If we temporarily started the recorder for capture-only, stop it now
    if (!recording && recorder.isActive) {
      recorder.stop();
    }
  }, [recording]);

  return {
    steps, recording, replacingIndex,
    startRecording, stopRecording, clearSteps, addStep,
    deleteStep, reRecordStep, reorderCandidateAt, removeCandidateAt,
    moveStep, updateStep, updateCandidateAt, addCandidateAt,
    reorderStepsByIds, loadSteps,
    addNestedStep, deleteNestedStep, moveNestedStep, updateNestedStep,
    startCapture, stopCapture,
  };
}

/* ── useReplayer ──────────────────────────────────────────────────────────────
 * Quick sanity-check replay for the Record tab.
 * Does NOT save a Run record to Dexie — that is Execution tab's job.
 * ──────────────────────────────────────────────────────────────────────────── */

function useReplayer() {
  const [replaying, setReplaying] = useState(false);
  const [results, setResults] = useState<ReplayStepResult[] | null>(null);
  const [executingIndex, setExecutingIndex] = useState<number | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [stepDelayMs, setStepDelayMs] = useState(800);
  const ctrlRef = useRef<import("./device/replayer.js").ExecutionController | null>(null);

  const replay = useCallback(async (
    steps: readonly IRStep[],
    adb: Adb,
    delay: number,
    scriptRunnerFn?: (code: string, vars: Record<string, unknown>) => Promise<ScriptResult>,
    sharedFlowLoaderFn?: (id: string) => Promise<{ name: string; steps: IRStep[]; parameters: { name: string; defaultValue?: string }[] } | undefined>,
  ) => {
    if (steps.length === 0) return;
    const { makeController } = await import("./device/replayer.js");
    const ctrl = makeController();
    ctrlRef.current = ctrl;
    setReplaying(true);
    setResults([]);
    setExecutingIndex(null);
    setReplayError(null);

    const acc: ReplayStepResult[] = [];
    try {
      await replaySteps([...steps], adb, {
        stepDelayMs: delay,
        controller: ctrl,
        scriptRunner: scriptRunnerFn,
        sharedFlowLoader: sharedFlowLoaderFn,
        onStepStart: (idx) => setExecutingIndex(idx),
        onStepResult: (result) => { acc.push(result); setResults([...acc]); },
      });
    } catch (err) {
      setReplayError(err instanceof Error ? err.message : String(err));
    } finally {
      setReplaying(false);
      setExecutingIndex(null);
      ctrlRef.current = null;
    }
  }, []);

  const stopReplay = useCallback(() => {
    if (ctrlRef.current) ctrlRef.current.stopped = true;
  }, []);

  return { replaying, results, executingIndex, replayError, replay, stopReplay, stepDelayMs, setStepDelayMs };
}

/* ── Root component ───────────────────────────────────────────────────────── */

export default function App() {
  const { state, connect, disconnect } = useDeviceSession();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { iframeRef: sandboxRef, scriptRunner } = useScriptSandbox();

  const adb = state.status === "connected" ? state.adb : null;
  const { mirrorState, stopMirror, restartMirror } = useScrcpyMirror(adb, canvasRef);

  // Fetch Android version once on connect
  const [androidVersion, setAndroidVersion] = useState("");
  useEffect(() => {
    if (!adb) { setAndroidVersion(""); return; }
    adb.subprocess.noneProtocol.spawnWaitText(["getprop", "ro.build.version.release"])
      .then((v) => setAndroidVersion(v.trim()))
      .catch(() => setAndroidVersion(""));
  }, [adb]);
  const { nodes, loading, error: treeError, dump } = useDumpTree();
  const {
    steps, recording, replacingIndex,
    startRecording, stopRecording, clearSteps, addStep,
    deleteStep, reRecordStep, reorderCandidateAt, removeCandidateAt,
    moveStep, updateStep, updateCandidateAt, addCandidateAt,
    reorderStepsByIds, loadSteps,
    addNestedStep, deleteNestedStep, moveNestedStep, updateNestedStep,
    startCapture, stopCapture,
  } = useRecorder(adb, canvasRef);
  const { replaying, results, executingIndex, replayError, replay, stopReplay, stepDelayMs, setStepDelayMs } = useReplayer();

  // Ref that always holds the current steps — lets the keyboard effect read the
  // latest value without going stale inside a useEffect closure.
  const latestStepsRef = useRef<readonly IRStep[]>(steps);
  const latestUpdateStepRef = useRef(updateStep);
  useEffect(() => { latestStepsRef.current = steps; }, [steps]);
  useEffect(() => { latestUpdateStepRef.current = updateStep; }, [updateStep]);

  // ── Quick Add Step ────────────────────────────────────────────────────────
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [capturing, setCapturing]       = useState(false);

  function handleStartCapture(callback: (sel: Selector) => void) {
    // If the user is in Inspect mode, silently switch to Drive first — capture
    // only works when the recorder's listeners are on the canvas, not the inspector's.
    if (canvasMode === "inspect") switchToDrive();
    setCapturing(true);
    startCapture((sel) => { setCapturing(false); callback(sel); });
  }
  function handleStopCapture() { setCapturing(false); stopCapture(); }

  // ── Phase 3b: 4-tab navigation ────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<StudioTab>("record");
  const [activeTestCase, setActiveTestCase] = useState<TestCase | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  // When editing a function's steps in the Recorder, track which function we're editing
  const [activeFunctionId, setActiveFunctionId] = useState<string | null>(null);
  const [activeFunctionName, setActiveFunctionName] = useState<string>("");
  const [runQueue, setRunQueue] = useState<TestCase[]>([]);
  const [externalRipple, setExternalRipple] = useState<{ deviceX: number; deviceY: number; seq: number } | null>(null);
  const rippleSeqRef = useRef(0);

  function handleStepRipple(deviceX: number, deviceY: number) {
    setExternalRipple({ deviceX, deviceY, seq: ++rippleSeqRef.current });
  }

  // ── Phase 3c: Inspect Mode ────────────────────────────────────────────────
  const [canvasMode, setCanvasMode] = useState<CanvasMode>("drive");
  const { inspectResult, inspecting, startInspecting, stopInspecting } = useInspector();

  // Switch mode: detach/attach the correct canvas listener
  const switchToInspect = useCallback(() => {
    if (!adb || !canvasRef.current) return;
    stopRecording();                                // detach recorder listener
    startInspecting(adb, canvasRef.current);        // attach inspector listener
    setCanvasMode("inspect");
  }, [adb, canvasRef, stopRecording, startInspecting]);

  const switchToDrive = useCallback(() => {
    stopInspecting();                               // detach inspector listener
    setCanvasMode("drive");
    // Note: user must click "Start Recording" again to re-attach recorder
  }, [stopInspecting]);

  // Assist mode: detach inspector if active. Unlike Drive/Inspect, Assist
  // doesn't forward canvas taps into recorded steps — it only needs "🎯 Pick
  // element" (setCaptureMode), which is already mode-independent (Custom
  // Commands / QuickAddStep use it today regardless of canvasMode).
  const switchToAssist = useCallback(() => {
    stopInspecting();
    setCanvasMode("assist");
  }, [stopInspecting]);

  // Alt key: hold = inspect, release = drive (when streaming)
  useEffect(() => {
    const mirrorStreaming = mirrorState.status === "streaming";
    if (!mirrorStreaming) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Alt" && !e.repeat && canvasMode === "drive") {
        e.preventDefault();
        switchToInspect();
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === "Alt" && canvasMode === "inspect") {
        e.preventDefault();
        switchToDrive();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [mirrorState.status, canvasMode, switchToInspect, switchToDrive]);

  const deviceInfo = {
    deviceModel: state.status === "connected" ? (state.deviceName ?? "") : "",
    deviceSerial: state.status === "connected" ? (state.serial ?? "") : "",
    androidVersion,
  };

  // Load test from Test Cases tab → switch to Record tab
  function handleLoadTestCase(tc: TestCase) {
    if (steps.length > 0 && !confirm(`Load "${tc.name}"? Unsaved steps will be lost.`)) return;
    const flow = tc.ir.flows[0];
    if (flow) loadSteps(flow.steps);
    setActiveTestCase(tc);
    setActiveFunctionId(null);
    setActiveFunctionName("");
    setActiveTab("record");
  }

  // "Run Selected" from Test Cases tab → pre-load queue + switch to Execution
  function handleRunSelected(tests: TestCase[]) {
    setRunQueue(tests);
    setActiveTab("execution");
  }

  function handleEditFunction(fn: import("./db/schema.js").SharedFlow) {
    const hasUnsaved = steps.length > 0 && (activeTestCase || activeFunctionId);
    if (hasUnsaved && !confirm(`Load "${fn.name}" steps? Any unsaved changes will be lost.`)) return;
    loadSteps(fn.steps);
    setActiveTestCase(null);
    setActiveFunctionId(fn.id);
    setActiveFunctionName(fn.name);
    setActiveTab("record");
  }

  async function handleSaveToFunction() {
    if (!activeFunctionId) return;
    const existing = await studioDb.sharedFlows.get(activeFunctionId);
    if (!existing) { alert("Function not found — it may have been deleted."); return; }
    await studioDb.sharedFlows.put({ ...existing, steps: [...steps], updatedAt: Date.now() });
    setActiveFunctionId(null);
    setActiveFunctionName("");
    clearSteps();
    setActiveTab("functions");
  }

  function handleCancelFunctionEdit() {
    if (!confirm(
      `Leave the Function Editor?\n\nYou are currently editing "${activeFunctionName}".\n` +
      `Your step changes will be DISCARDED.\n\nClick OK to discard and leave, or Cancel to keep editing.`
    )) return;
    setActiveFunctionId(null);
    setActiveFunctionName("");
    clearSteps();
  }

  function handleSaved(tc: TestCase) {
    setActiveTestCase(tc);
    setShowSaveModal(false);
  }

  function handleLoadTestCaseFromFile() {
    // Prompt before overwriting if current steps would be lost
    if (steps.length > 0) {
      const msg = activeTestCase
        ? `Loading a file will replace the current test "${activeTestCase.name}". Continue?`
        : `Loading a file will discard your current ${steps.length} step${steps.length !== 1 ? "s" : ""}. Continue?`;
      if (!confirm(msg)) return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.zip,application/json,application/zip";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // ── ZIP: extract tests, let user choose which one to load ──────────────
      if (file.name.endsWith(".zip")) {
        let entries;
        try { entries = await extractJsonsFromZip(file); }
        catch { alert(`Could not read ZIP: "${file.name}". Make sure it was exported from Mimiq.`); return; }

        if (entries.length === 0) {
          alert(`No test cases found in "${file.name}".`); return;
        }

        // Validate all entries
        const valid: Array<{ name: string; text: string; stepCount: number }> = [];
        for (const entry of entries) {
          try {
            const parsed = JSON.parse(entry.jsonText) as unknown;
            const result = validateIR(parsed);
            if (result.ok) {
              const ir = result.ir;
              const flow = ir.flows.find((f) => f.entry) ?? ir.flows[0];
              valid.push({ name: ir.meta.name, text: entry.jsonText, stepCount: flow?.steps.length ?? 0 });
            }
          } catch { /* skip invalid */ }
        }

        if (valid.length === 0) {
          alert(`No valid test cases found in "${file.name}".`); return;
        }

        if (valid.length === 1) {
          // Only one — load directly
          const parsed = JSON.parse(valid[0]!.text) as unknown;
          const result = validateIR(parsed);
          if (result.ok) {
            const ir = result.ir;
            const flow = ir.flows.find((f) => f.entry) ?? ir.flows[0];
            if (flow) { clearSteps(); loadSteps(flow.steps); setActiveTestCase(null); }
          }
          return;
        }

        // Multiple — show a simple prompt to pick one
        const choices = valid.map((v, i) => `${i + 1}. ${v.name} (${v.stepCount} steps)`).join("\n");
        const raw = prompt(`Found ${valid.length} test cases in "${file.name}".\nEnter the number to load into the recorder:\n\n${choices}`);
        if (!raw) return;
        const idx = parseInt(raw.trim()) - 1;
        if (idx < 0 || idx >= valid.length) { alert("Invalid selection."); return; }
        const chosen = valid[idx]!;
        const parsed2 = JSON.parse(chosen.text) as unknown;
        const result2 = validateIR(parsed2);
        if (result2.ok) {
          const ir2 = result2.ir;
          const flow2 = ir2.flows.find((f) => f.entry) ?? ir2.flows[0];
          if (flow2) { clearSteps(); loadSteps(flow2.steps); setActiveTestCase(null); }
        }
        return;
      }

      // ── Single JSON (original flow) ────────────────────────────────────────
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        let parsed: unknown;
        try { parsed = JSON.parse(text); }
        catch {
          alert(`"${file.name}" is not valid JSON.\n\nPlease check the file and try again.`);
          return;
        }

        const result = validateIR(parsed);
        if (!result.ok) {
          const issues = result.error.issues.slice(0, 5);
          const details = issues.map((iss) => `• ${iss.path.join(".") || "(root)"}: ${iss.message}`).join("\n");
          const more = result.error.issues.length > 5 ? `\n… and ${result.error.issues.length - 5} more issues.` : "";
          alert(`"${file.name}" is not a valid Mimiq test file.\n\nProblems found:\n${details}${more}\n\nExport using "Download Test Case" to get a valid file.`);
          return;
        }

        const ir = result.ir;
        const entryFlow = ir.flows.find((f) => f.entry) ?? ir.flows[0];
        if (!entryFlow || entryFlow.steps.length === 0) {
          alert(`"${file.name}" contains no steps to load.`); return;
        }

        clearSteps();
        loadSteps(entryFlow.steps);
        setActiveTestCase(null);

        const otherFlowCount = ir.flows.length - 1;
        if (otherFlowCount > 0) {
          alert(`Loaded "${ir.meta.name}" (${entryFlow.steps.length} step${entryFlow.steps.length !== 1 ? "s" : ""}).\n\nNote: This file has ${otherFlowCount} additional flow${otherFlowCount > 1 ? "s" : ""}. Only the entry flow is shown in the recorder.`);
        }
      };

      reader.onerror = () => { alert(`Could not read "${file.name}". Please try again.`); };
      reader.readAsText(file);
    };

    input.click();
  }

  function handleNewTest() {
    // Confirm if there are unsaved steps or a loaded test with edits
    if (steps.length > 0) {
      const msg = activeTestCase
        ? `Discard unsaved changes to "${activeTestCase.name}" and start a new test?`
        : `Discard ${steps.length} unsaved step${steps.length !== 1 ? "s" : ""} and start a new test?`;
      if (!confirm(msg)) return;
    }
    clearSteps();
    setActiveTestCase(null);
  }

  const exportIR = useCallback(() => {
    if (steps.length === 0) return;
    const launchStep = steps.find((s) => s.type === "launchApp");
    const appPkg = launchStep?.type === "launchApp" ? launchStep.packageName : undefined;
    const ir: TestIR = {
      irVersion: IR_VERSION,
      meta: { id: generateId(), name: activeTestCase?.name ?? "Recorded Test", tags: ["recorded", "studio"], createdBy: "studio-recorder" },
      config: {
        targets: ["android"],
        ...(appPkg ? { app: { packageName: appPkg } } : {}),
        env: {},
        dataSources: {},
        interruptionPolicy: { permissions: "allowAll", crashOnAnr: true },
        healing: { maxTierUsed: 1, minConfidence: 0.75, mode: "suggest" },
        timeouts: { step: 15000 },
      },
      flows: [{ id: generateId(), name: "Recorded Flow", entry: true, steps: [...steps] }],
      hooks: { beforeEach: [], afterEach: [] },
    };
    const blob = new Blob([JSON.stringify(ir, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(activeTestCase?.name ?? "recorded-test").replace(/\s+/g, "-").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [steps, activeTestCase]);

  const mirrorStreaming = mirrorState.status === "streaming";
  // Mirror is visible only on Record + Execution tabs when connected
  const showMirror = (activeTab === "record" || activeTab === "execution") && state.status === "connected";

  // Desktop keyboard → phone: forward key events to ADB while streaming in Drive mode.
  // Also records TypeText / pressKey steps when recording is active.
  // Encoding fix: adb input text receives args via ADB protocol (not shell), so raw
  // characters work directly. Space needs %s (ADB input text convention). Do NOT use
  // encodeURIComponent — that was converting '@' to '%40' which appeared literally.
  useEffect(() => {
    if (!mirrorStreaming || !adb || canvasMode !== "drive") return;

    const ADB_KEYCODE: Record<string, string> = {
      Backspace: "KEYCODE_DEL", Delete: "KEYCODE_FORWARD_DEL",
      Enter: "KEYCODE_ENTER", Tab: "KEYCODE_TAB",
      ArrowUp: "KEYCODE_DPAD_UP", ArrowDown: "KEYCODE_DPAD_DOWN",
      ArrowLeft: "KEYCODE_DPAD_LEFT", ArrowRight: "KEYCODE_DPAD_RIGHT",
      Home: "KEYCODE_MOVE_HOME", End: "KEYCODE_MOVE_END",
      Escape: "KEYCODE_ESCAPE", PageUp: "KEYCODE_PAGE_UP", PageDown: "KEYCODE_PAGE_DOWN",
    };
    const RECORD_PRESSKEY: Record<string, import("@mimiq/ir").PressKeyName> = {
      Enter: "enter", Tab: "tab", Backspace: "delete",
    };

    // Accumulate printable chars into a single typeText step (debounced)
    let pendingText = "";
    let debounce: ReturnType<typeof setTimeout> | null = null;

    function flushPending() {
      if (pendingText && recording) {
        const curSteps = latestStepsRef.current;
        const lastStep = curSteps[curSteps.length - 1];
        if (lastStep?.type === "typeText") {
          // Extend the previous typeText step instead of creating a new one —
          // this keeps "meena" + "jeyaprakash32" as one step rather than two.
          latestUpdateStepRef.current(curSteps.length - 1, {
            ...lastStep,
            text: lastStep.text + pendingText,
          });
        } else {
          addStep({ id: generateId(), type: "typeText", text: pendingText });
        }
      }
      pendingText = "";
      debounce = null;
    }

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      if (e.key.length === 1) {
        e.preventDefault();
        // Fix: pass raw character — space needs %s, everything else as-is.
        // encodeURIComponent was wrong: it encoded '@' as '%40' which adb printed literally.
        const raw = e.key === " " ? "%s" : e.key === "%" ? "%%" : e.key;
        adb!.subprocess.noneProtocol.spawnWaitText(["input", "text", raw]).catch(() => {});
        // Accumulate for typeText step when recording
        if (recording) {
          pendingText += e.key;
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(flushPending, 1200);
        }
      } else {
        const kc = ADB_KEYCODE[e.key];
        if (!kc) return;
        e.preventDefault();

        // Backspace while accumulating: shrink pending text
        if (e.key === "Backspace" && recording && pendingText) {
          pendingText = pendingText.slice(0, -1);
          adb!.subprocess.noneProtocol.spawnWaitText(["input", "keyevent", kc]).catch(() => {});
          return;
        }

        // Any other special key: flush pending text first, then handle key
        if (debounce) { clearTimeout(debounce); debounce = null; }
        flushPending();

        adb!.subprocess.noneProtocol.spawnWaitText(["input", "keyevent", kc]).catch(() => {});
        // Record special keys as pressKey steps
        if (recording) {
          const pk = RECORD_PRESSKEY[e.key];
          if (pk) addStep({ id: generateId(), type: "pressKey", key: pk });
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (debounce) clearTimeout(debounce);
      flushPending(); // commit any pending chars on cleanup
    };
  // recording and addStep must be in deps so we always use the current recorder state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mirrorStreaming, adb, canvasMode, recording, addStep]);

  // Drive-mode passthrough: forward canvas taps to the device even when not recording.
  // Detached when: capturing is active (capture mode needs a stable screen for dumpTree),
  // or recording, or inspect mode.
  useEffect(() => {
    if (!mirrorStreaming || recording || canvasMode !== "drive" || capturing || !adb || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const deviceW = mirrorState.status === "streaming" ? mirrorState.width : 1080;
    const deviceH = mirrorState.status === "streaming" ? mirrorState.height : 2400;

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      const rect = canvas.getBoundingClientRect();
      const x = Math.round(((e.clientX - rect.left) / rect.width)  * deviceW);
      const y = Math.round(((e.clientY - rect.top)  / rect.height) * deviceH);
      adb!.subprocess.noneProtocol.spawnWaitText(["input", "tap", String(x), String(y)]).catch(() => {});
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    return () => canvas.removeEventListener("pointerdown", onPointerDown);
  }, [mirrorStreaming, recording, canvasMode, capturing, adb, canvasRef, mirrorState]);

  return (
    <div className="app-shell">
      {/* ── Collapsible sidebar navigation ── */}
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ── Main workspace ── */}
      <div className="app-workspace">

        {/* ── Top bar ── */}
        <header className="app-topbar">
          <div className="app-topbar-left">
            {activeTab === "record" && activeTestCase && steps.length > 0 && (
              <span className="app-active-test" title={`Active: ${activeTestCase.name}`}>
                📄 {activeTestCase.name}
              </span>
            )}
            {activeTab === "record" && !activeTestCase && steps.length > 0 && !activeFunctionId && (
              <span className="app-active-test app-active-test--unsaved">📄 Unsaved test</span>
            )}
            {activeTab === "record" && activeFunctionId && (
              <span className="app-active-test" style={{ color: "var(--color-purple)" }} title={`Editing function: ${activeFunctionName}`}>
                ♻ {activeFunctionName}
              </span>
            )}
          </div>
          <ConnectButton state={state} onConnect={connect} onDisconnect={disconnect} />
        </header>

        {/* Connection Doctor — shown on Record + Execution tabs */}
        {state.status === "error" && (activeTab === "record" || activeTab === "execution") && (
          <ConnectionDoctorPanel error={state.error} onRetry={connect} />
        )}

        {/* ── Tab content area ── */}
        {/* app-tab-area--landscape: applied when phone is in landscape orientation */}
        <div className={`app-tab-area${mirrorState.status === "streaming" && mirrorState.width > mirrorState.height ? " app-tab-area--landscape" : ""}`}>

          {/* Mirror: ALWAYS mounted — CSS visibility only — canvas must never unmount */}
          <div className={`mirror-outer${showMirror ? "" : " mirror-outer--hidden"}`}>
            <DeviceMirror
              canvasRef={canvasRef}
              mirrorState={mirrorState}
              onStop={stopMirror}
              onRestart={adb ? restartMirror : undefined}
              inspectHighlight={
                canvasMode === "inspect" && inspectResult
                  ? { bounds: inspectResult.node.bounds, deviceW: mirrorState.status === "streaming" ? mirrorState.width : 0, deviceH: mirrorState.status === "streaming" ? mirrorState.height : 0 }
                  : undefined
              }
              externalRipple={externalRipple}
            />
          </div>

          {/* ── Record tab content ── */}
          {activeTab === "record" && (
            <div className="tab-content tab-content--record">

              {/* Landing page when phone is not connected */}
              {state.status !== "connected" && (
                <RecordLandingPage onConnect={connect} />
              )}

              {state.status === "connected" && (
                <div className="device-connected-banner">
                  <span className="status-dot status-dot--ok" />
                  Connected to <strong>{state.deviceName}</strong>
                  <span className="serial"> ({state.serial})</span>
                </div>
              )}

              {mirrorStreaming && state.status === "connected" && (
                <div className="studio-side-panel">
                  <RecorderPanel
                    adb={state.adb}
                    recording={recording}
                    replacingIndex={replacingIndex}
                    steps={steps}
                    canvasMode={canvasMode}
                    onSwitchToInspect={switchToInspect}
                    onSwitchToDrive={switchToDrive}
                    onSwitchToAssist={switchToAssist}
                    onStartRecording={startRecording}
                    onStopRecording={stopRecording}
                    onClearSteps={clearSteps}
                    onAddStep={addStep}
                    onDeleteStep={deleteStep}
                    onReRecordStep={reRecordStep}
                    onReorderCandidate={reorderCandidateAt}
                    onRemoveCandidate={removeCandidateAt}
                    onMoveStep={moveStep}
                    onUpdateStep={updateStep}
                    onExportIR={exportIR}
                    onLoadTestCase={handleLoadTestCaseFromFile}
                    onUpdateCandidate={updateCandidateAt}
                    onAddCandidate={addCandidateAt}
                    onReorderStepsByIds={reorderStepsByIds}
                    onSaveTest={() => setShowSaveModal(true)}
                    onNewTest={handleNewTest}
                    activeFunctionId={activeFunctionId}
                    activeFunctionName={activeFunctionName}
                    onSaveToFunction={() => void handleSaveToFunction()}
                    onCancelFunctionEdit={handleCancelFunctionEdit}
                    showQuickAdd={showQuickAdd}
                    onShowQuickAdd={setShowQuickAdd}
                    onStartCapture={handleStartCapture}
                    onStopCapture={handleStopCapture}
                    capturing={capturing}
                    activeTestCase={activeTestCase}
                    deviceWidth={mirrorState.status === "streaming" ? mirrorState.width : undefined}
                    deviceHeight={mirrorState.status === "streaming" ? mirrorState.height : undefined}
                    onAddNestedStep={addNestedStep}
                    onDeleteNestedStep={deleteNestedStep}
                    onMoveNestedStep={moveNestedStep}
                    onUpdateNestedStep={updateNestedStep}
                  />
                  {/* Inspect panel — shown instead of replay/monaco when in inspect mode */}
                  {canvasMode === "inspect" ? (
                    <InspectPanel
                      result={inspectResult}
                      loading={inspecting}
                      deviceW={mirrorState.status === "streaming" ? mirrorState.width : 1080}
                      deviceH={mirrorState.status === "streaming" ? mirrorState.height : 2400}
                      onAddStep={(step) => { addStep(step); switchToDrive(); }}
                    />
                  ) : (
                  <>
                  {steps.length > 0 && (
                    <>
                      <ReplayPanel
                        adb={adb!}
                        steps={steps}
                        replaying={replaying}
                        results={results}
                        executingIndex={executingIndex}
                        error={replayError}
                        stepDelayMs={stepDelayMs}
                        onChangeDelay={setStepDelayMs}
                        onReplay={(s, a) => replay(s, a, stepDelayMs, scriptRunner, loadSharedFlow)}
                        onStopReplay={stopReplay}
                      />
                      <MonacoView steps={steps} name={activeTestCase?.name ?? "Recorded Test"} />
                    </>
                  )}
                  {state.status === "connected" && (
                    <TreePanel adb={state.adb} nodes={nodes} loading={loading} error={treeError} onDump={dump} />
                  )}
                  </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Test Cases tab ── */}
          {activeTab === "testcases" && (
            <div className="tab-content tab-content--full">
              <TestCasesTab
                activeTestCaseId={activeTestCase?.id ?? null}
                onLoadTestCase={handleLoadTestCase}
                onRunSelected={handleRunSelected}
              />
            </div>
          )}

          {/* ── Functions tab ── */}
          {activeTab === "functions" && (
            <div className="tab-content tab-content--full">
              <FunctionsTab onEditFunction={handleEditFunction} />
            </div>
          )}

          {/* ── Execution tab — always mounted so progress state survives tab switches ── */}
          <div className={`tab-content tab-content--execution${activeTab !== "execution" ? " tab-content--hidden" : ""}`}>
            <ExecutionTab
              adb={adb}
              runQueue={runQueue}
              onQueueChange={setRunQueue}
              stepDelayMs={stepDelayMs}
              deviceInfo={deviceInfo}
              onSwitchToReports={() => setActiveTab("reports")}
              onStepRipple={handleStepRipple}
              scriptRunner={scriptRunner}
              sharedFlowLoader={loadSharedFlow}
            />
          </div>

          {/* ── Reports tab ── */}
          {activeTab === "reports" && (
            <div className="tab-content tab-content--full">
              <ReportsTab />
            </div>
          )}

          {/* ── Settings tab ── */}
          {/* ── Wiki tab ── */}
          {activeTab === "wiki" && (
            <div className="tab-content tab-content--full tab-content--wiki">
              <WikiPage />
            </div>
          )}

          {/* ── Settings tab ── */}
          {activeTab === "settings" && (
            <div className="tab-content tab-content--full tab-content--settings">
              <SettingsPage />
            </div>
          )}
        </div>
      </div>

      {/* Hidden script sandbox iframe — allow-scripts only, no DOM access */}
      <iframe
        ref={sandboxRef}
        sandbox="allow-scripts"
        // eslint-disable-next-line react/no-danger
        srcDoc={SANDBOX_SRCDOC}
        style={{ display: "none" }}
        title="Mimiq Script Sandbox"
        aria-hidden="true"
      />

      {/* Save test modal (Record tab) */}
      {showSaveModal && (
        <SaveTestModal
          steps={steps}
          existingId={activeTestCase?.id}
          existingName={activeTestCase?.name}
          existingSuiteId={activeTestCase?.suiteId}
          existingTags={activeTestCase?.tags}
          onSaved={handleSaved}
          onCancel={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}

/* ── CommandToolbar ───────────────────────────────────────────────────────── */

type ActiveForm =
  | { kind: "launchApp" | "stopApp" | "typeText" | "openUrl" | "deepLink"; }
  | { kind: "doubleTap" | "longPress" | "clearText" | "waitFor" | "scrollUntilVisible" | "assert"; }
  | { kind: "wait"; }
  | { kind: "swipe"; }
  | { kind: "setDeviceState"; }
  | { kind: "getVariable"; }
  | { kind: "randomGenerator"; }
  | { kind: "holdSwipe"; }
  | { kind: "getAttribute"; }
  | { kind: "evaluate"; }
  | { kind: "callFunction"; }
  | { kind: "getCount"; }
  | null;

interface CommandToolbarProps {
  adb: Adb;
  onAddStep: (step: IRStep) => void;
  deviceWidth?: number;
  deviceHeight?: number;
  onStartCapture?: (cb: (sel: Selector) => void) => void;
  onStopCapture?: () => void;
  capturing?: boolean;
}

// Human-readable form titles for each command kind
const FORM_TITLES: Record<string, string> = {
  launchApp: "Open App", stopApp: "Close App", typeText: "Type Text",
  wait: "Wait (Duration)", doubleTap: "Double Tap", longPress: "Long Press",
  clearText: "Clear Text", waitFor: "Wait For Element", scrollUntilVisible: "Scroll To Element",
  swipe: "Swipe Custom", assert: "Verify (Assert)", getVariable: "Get Variable",
  randomGenerator: "Random Value", holdSwipe: "Hold & Scroll", getAttribute: "Get Attribute",
  getCount: "Get Count", evaluate: "Compare Values", callFunction: "Call Function",
  setDeviceState: "Device State", openUrl: "Open URL", deepLink: "Deep Link",
};

// Element-based commands that benefit from mirror capture
const ELEMENT_FORM_KINDS = new Set(["doubleTap","longPress","clearText","waitFor","scrollUntilVisible","assert","holdSwipe","getAttribute","getCount"]);

function CommandToolbar({ adb, onAddStep, deviceWidth = 1080, deviceHeight = 2340, onStartCapture, onStopCapture, capturing }: CommandToolbarProps) {
  const [activeForm, setActiveForm] = useState<ActiveForm>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [inputVal, setInputVal] = useState("");
  const [input2, setInput2] = useState("");
  const [numVal, setNumVal] = useState(1000);
  const [dirVal, setDirVal] = useState<"up"|"down"|"left"|"right">("down");
  const [stateVal, setStateVal] = useState<"visible"|"hidden"|"enabled"|"disabled">("visible");
  const [wifiVal, setWifiVal] = useState<boolean | undefined>(undefined);
  const [airplaneVal, setAirplaneVal] = useState<boolean | undefined>(undefined);
  const [locatorStrategy, setLocatorStrategy] = useState<LocatorStrategy>("text");
  const [locatorValue, setLocatorValue] = useState("");
  const [assertionPreset, setAssertionPreset] = useState<"visible"|"notVisible"|"enabled">("visible");
  const [randomType, setRandomType] = useState<"string"|"number"|"alphanumeric">("alphanumeric");
  const [randomLength, setRandomLength] = useState(8);
  const [randomMin, setRandomMin]     = useState(1);
  const [randomMax, setRandomMax]     = useState(999999);
  const [holdMs, setHoldMs]           = useState<number|"">(800);
  const [sensitiveInput, setSensitiveInput] = useState(false);
  const [holdDistance, setHoldDistance] = useState<number|"">(400);
  // Get Attribute state
  const [attrAttr, setAttrAttr] = useState("enabled");
  const ATTR_OPTIONS = ["text","resourceId","contentDesc","className","pkg","enabled","checked","clickable","focused","selected","scrollable","longClickable","password","checkable","boundsLeft","boundsTop","boundsRight","boundsBottom","boundsWidth","boundsHeight"] as const;
  // Call Function state
  const [cfSelectedId, setCfSelectedId] = useState<string>("");
  const [cfArgs, setCfArgs] = useState<Record<string, string>>({});
  const sharedFunctions = useLiveQuery(() => import("./db/index.js").then(m => m.db.sharedFlows.orderBy("name").toArray()), []) ?? [];
  // Evaluate step state
  const OPERATORS = ["==","!=",">","<",">=","<=","contains","notContains","startsWith","endsWith","matches","isEmpty","isNotEmpty"] as const;
  const [evalLeft, setEvalLeft]     = useState("");
  const [evalOp, setEvalOp]         = useState<string>("==");
  const [evalRight, setEvalRight]   = useState("");
  const [evalStoreAs, setEvalStoreAs] = useState("");
  const [evalFailIfFalse, setEvalFailIfFalse] = useState(false);
  const [evalTrim, setEvalTrim]     = useState(false);
  const [evalIgnoreCase, setEvalIgnoreCase] = useState(false);
  const [evalParseNumber, setEvalParseNumber] = useState(false);

  const [packages, setPackages] = useState<string[]>([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const pkgFetchedRef = useRef(false);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Close category panel on click outside
  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setActiveCategory(null);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  function toggleAdvanced() {
    const next = activeCategory !== "flow";
    setActiveCategory(next ? "flow" : null);
    try { localStorage.setItem("mimiq-toolbar-advanced", String(next)); } catch { /* ignore */ }
  }

  async function loadPackages() {
    if (pkgFetchedRef.current) return;
    pkgFetchedRef.current = true;
    setPkgLoading(true);
    try {
      const raw = await adb.subprocess.noneProtocol.spawnWaitText(["pm", "list", "packages"]);
      setPackages(raw.split("\n").map((l) => l.trim()).filter((l) => l.startsWith("package:")).map((l) => l.slice("package:".length)).sort());
    } catch { /* ignore */ } finally { setPkgLoading(false); }
  }

  function open(kind: NonNullable<ActiveForm>["kind"], defaultNum = 1000) {
    setActiveForm({ kind } as ActiveForm);
    setActiveCategory(null); // close category panel; form takes over
    setInputVal("");
    setInput2("");
    setNumVal(defaultNum);
    setLocatorStrategy("text");
    setLocatorValue("");
    if (kind === "launchApp" || kind === "stopApp") void loadPackages();
  }

  function cancel() { setActiveForm(null); setInputVal(""); setInput2(""); setLocatorValue(""); }

  // Add step and close the category panel
  function addDirect(step: IRStep) { onAddStep(step); setActiveCategory(null); }

  function addKey(key: PressKeyName) { addDirect({ id: generateId(), type: "pressKey", key }); }

  function addScrollStep(direction: "up" | "down" | "left" | "right") {
    const w = deviceWidth;
    const h = deviceHeight;
    const presets: Record<string, [[number, number], [number, number]]> = {
      down:  [[w * 0.5, h * 0.7], [w * 0.5, h * 0.3]],
      up:    [[w * 0.5, h * 0.3], [w * 0.5, h * 0.7]],
      left:  [[w * 0.8, h * 0.5], [w * 0.2, h * 0.5]],
      right: [[w * 0.2, h * 0.5], [w * 0.8, h * 0.5]],
    };
    const [from, to] = presets[direction]!;
    addDirect({ id: generateId(), type: "swipe", from: [Math.round(from[0]), Math.round(from[1])], to: [Math.round(to[0]), Math.round(to[1])], durationMs: 300 });
  }

  function confirm() {
    if (!activeForm) return;
    const id = generateId();
    switch (activeForm.kind) {
      case "launchApp":
        if (inputVal.trim()) { onAddStep({ id, type: "launchApp", packageName: inputVal.trim() }); break; } return;
      case "stopApp":
        if (inputVal.trim()) { onAddStep({ id, type: "stopApp", packageName: inputVal.trim() }); break; } return;
      case "typeText":
        if (inputVal.length > 0) { onAddStep({ id, type: "typeText", text: inputVal, ...(sensitiveInput ? { sensitive: true } : {}) }); setSensitiveInput(false); break; } return;
      case "wait":
        if (numVal > 0) { onAddStep({ id, type: "wait", durationMs: numVal }); break; } return;
      case "openUrl":
        if (inputVal.trim()) { onAddStep({ id, type: "openUrl", url: inputVal.trim() }); break; } return;
      case "deepLink":
        if (inputVal.trim()) { onAddStep({ id, type: "deepLink", url: inputVal.trim(), ...(input2.trim() ? { packageName: input2.trim() } : {}) }); break; } return;
      case "doubleTap": {
        if (!locatorValue.trim()) return;
        const cDT = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "doubleTap", selector: { description: locatorValue.trim(), candidates: [cDT] } }); break;
      }
      case "longPress": {
        if (!locatorValue.trim()) return;
        const cLP = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "longPress", selector: { description: locatorValue.trim(), candidates: [cLP] }, durationMs: numVal }); break;
      }
      case "clearText": {
        if (!locatorValue.trim()) return;
        const cCT = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "clearText", selector: { description: locatorValue.trim(), candidates: [cCT] } }); break;
      }
      case "waitFor": {
        if (!locatorValue.trim()) return;
        const cWF = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "waitFor", selector: { description: locatorValue.trim(), candidates: [cWF] }, state: stateVal, timeoutMs: numVal }); break;
      }
      case "scrollUntilVisible": {
        if (!locatorValue.trim()) return;
        const cSU = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "scrollUntilVisible", selector: { description: locatorValue.trim(), candidates: [cSU] }, direction: dirVal, maxScrolls: numVal }); break;
      }
      case "assert": {
        if (!locatorValue.trim()) return;
        const cAS = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "assert", selector: { description: locatorValue.trim(), candidates: [cAS] }, assertion: assertionPreset }); break;
      }
      case "swipe": {
        const w = deviceWidth; const h = deviceHeight;
        const presets: Record<string, [[number,number],[number,number]]> = {
          down:  [[w*0.5,h*0.7],[w*0.5,h*0.3]], up: [[w*0.5,h*0.3],[w*0.5,h*0.7]],
          left:  [[w*0.8,h*0.5],[w*0.2,h*0.5]], right: [[w*0.2,h*0.5],[w*0.8,h*0.5]],
        };
        const [from, to] = presets[dirVal]!;
        onAddStep({ id, type: "swipe", from: [Math.round(from[0]),Math.round(from[1])], to: [Math.round(to[0]),Math.round(to[1])], durationMs: numVal });
        break;
      }
      case "setDeviceState":
        if (wifiVal === undefined && airplaneVal === undefined) return;
        onAddStep({ id, type: "setDeviceState", ...(wifiVal !== undefined ? { wifi: wifiVal } : {}), ...(airplaneVal !== undefined ? { airplane: airplaneVal } : {}) });
        break;
      case "getAttribute":
        if (!inputVal.trim() || !locatorValue.trim()) return;
        { const cGA = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
          onAddStep({ id, type: "setVariable", name: inputVal.trim(), source: { kind: "attribute", selector: { description: locatorValue.trim(), candidates: [cGA] }, attribute: attrAttr as typeof ATTR_OPTIONS[number] } } as import("@mimiq/ir").SetVariableStep); }
        break;
      case "evaluate": {
        if (!evalLeft.trim()) return;
        const needsRight = !["isEmpty","isNotEmpty"].includes(evalOp);
        const opts = (evalTrim || evalIgnoreCase || evalParseNumber) ? { trim: evalTrim || undefined, ignoreCase: evalIgnoreCase || undefined, parseNumber: evalParseNumber || undefined } : undefined;
        onAddStep({ id, type: "evaluate", left: evalLeft.trim(), operator: evalOp as import("@mimiq/ir").CompareOperator, right: (needsRight && evalRight.trim()) ? evalRight.trim() : undefined, options: opts, storeAs: evalStoreAs.trim() || undefined, failIfFalse: evalFailIfFalse || undefined } as import("@mimiq/ir").EvaluateStep);
        break;
      }
      case "callFunction": {
        if (!cfSelectedId) return;
        const fn = sharedFunctions.find(f => f.id === cfSelectedId);
        if (!fn) return;
        onAddStep({ id, type: "callFlow", sharedFlowId: fn.id, sharedFlowName: fn.name, args: { ...cfArgs } } as import("@mimiq/ir").CallFlowStep);
        break;
      }
      case "getCount": {
        if (!inputVal.trim() || !locatorValue.trim()) return;
        const gcand = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "setVariable", name: inputVal.trim(), source: { kind: "count", selector: { description: locatorValue.trim(), candidates: [gcand] } } } as import("@mimiq/ir").SetVariableStep);
        break;
      }
      case "getVariable":
        if (!inputVal.trim()) return;
        onAddStep({ id, type: "print", message: `${inputVal.trim()} = \${${inputVal.trim()}}`, level: "info" });
        break;
      case "randomGenerator":
        if (!inputVal.trim()) return;
        onAddStep({
          id, type: "setVariable", name: inputVal.trim(),
          source: randomType === "number"
            ? { kind: "random", randomType: "number", min: randomMin, max: randomMax }
            : { kind: "random", randomType, length: randomLength },
        });
        break;
      case "holdSwipe": {
        if (!locatorValue.trim()) return;
        const cHS = { strategy: locatorStrategy, value: locatorValue.trim(), addedBy: "user" as const };
        onAddStep({ id, type: "holdSwipe", selector: { description: locatorValue.trim(), candidates: [cHS] }, direction: dirVal, holdMs: holdMs === "" ? 800 : Math.max(100, holdMs), distance: holdDistance === "" ? 400 : Math.max(50, holdDistance) });
        break;
      }
    }
    cancel();
  }

  const isPkgForm = activeForm?.kind === "launchApp" || activeForm?.kind === "stopApp";

  // ── Category definitions ─────────────────────────────────────────────────
  const CATEGORIES = [
    { id: "app",      label: "App" },
    { id: "keys",     label: "Keys" },
    { id: "util",     label: "Util" },
    { id: "scroll",   label: "Scroll" },
    { id: "touch",    label: "Touch" },
    { id: "assert",   label: "Assert" },
    { id: "input",    label: "Input" },
    { id: "wait",     label: "Wait" },
    { id: "swipe",    label: "Swipe" },
    { id: "vars",     label: "Variables" },
    { id: "gesture",  label: "Gesture" },
    { id: "report",   label: "Report" },
    { id: "control",  label: "Control" },
    { id: "navigate", label: "Navigate" },
    { id: "device",   label: "Device" },
    { id: "flow",     label: "Flow" },
  ];

  function toggleCat(id: string) {
    setActiveCategory((prev) => prev === id ? null : id);
    setActiveForm(null);
  }

  return (
    <div className="command-toolbar" ref={toolbarRef}>
      {/* ═══ Category tab strip (always one row) ═══ */}
      <div className="ctb-strip">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            className={`ctb-chip${activeCategory === cat.id ? " ctb-chip--active" : ""}`}
            onClick={() => toggleCat(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ═══ Category panel (shown below strip when a category is active) ═══ */}
      {activeCategory && !activeForm && (
        <div className="ctb-panel">
          {activeCategory === "app" && (<>
            <button className="cmd-btn cmd-btn--app" title="Launch an app by package name. Optionally clear app data first." onClick={() => open("launchApp")}>Open App</button>
            <button className="cmd-btn cmd-btn--app" title="Force-stop an app by package name." onClick={() => open("stopApp")}>Close App</button>
          </>)}
          {activeCategory === "keys" && (<>
            <button className="cmd-btn" title="Press the Home button — returns to the launcher." onClick={() => addKey("home")}>Home</button>
            <button className="cmd-btn" title="Press the Back button — navigates back one screen." onClick={() => addKey("back")}>Back</button>
            <button className="cmd-btn" title="Press Enter / confirm the focused action." onClick={() => addKey("enter")}>Enter</button>
            <button className="cmd-btn" title="Press Tab — moves focus to the next field." onClick={() => addKey("tab")}>Tab</button>
            <button className="cmd-btn" title="Press Delete/Backspace — deletes one character." onClick={() => addKey("delete")}>Delete</button>
          </>)}
          {activeCategory === "util" && (<>
            <button className="cmd-btn" title="Pause execution for a fixed duration (milliseconds)." onClick={() => { open("wait"); setNumVal(1000); }}>Wait</button>
            <button className="cmd-btn" title="Take a screenshot. Saved to OPFS and shown in Reports." onClick={() => { addDirect({ id: generateId(), type: "screenshot" }); }}>Screenshot</button>
            <button className="cmd-btn" title="Type text into the currently focused field. Tap the input field first." onClick={() => open("typeText")}>Type Text</button>
          </>)}
          {activeCategory === "scroll" && (<>
            <button className="cmd-btn" title="Swipe Up — scrolls content downward." onClick={() => addScrollStep("up")}>↑ Swipe Up</button>
            <button className="cmd-btn" title="Swipe Down — scrolls content upward." onClick={() => addScrollStep("down")}>↓ Swipe Down</button>
            <button className="cmd-btn" title="Swipe Left — scrolls content rightward." onClick={() => addScrollStep("left")}>← Swipe Left</button>
            <button className="cmd-btn" title="Swipe Right — scrolls content leftward." onClick={() => addScrollStep("right")}>→ Swipe Right</button>
          </>)}
          {activeCategory === "touch" && (<>
            <button className="cmd-btn" title="Double-tap an element." onClick={() => open("doubleTap")}>Double Tap</button>
            <button className="cmd-btn" title="Long-press an element for a configurable duration (default 800ms)." onClick={() => { open("longPress"); setNumVal(800); }}>Long Press</button>
          </>)}
          {activeCategory === "assert" && (<>
            <button className="cmd-btn cmd-btn--assert" title="Assert that an element is visible on screen." onClick={() => { setAssertionPreset("visible"); open("assert"); }}>Verify Visible</button>
            <button className="cmd-btn cmd-btn--assert" title="Assert that an element is NOT visible on screen." onClick={() => { setAssertionPreset("notVisible"); open("assert"); }}>Verify Hidden</button>
            <button className="cmd-btn cmd-btn--assert" title="Assert that an element is enabled / interactive." onClick={() => { setAssertionPreset("enabled"); open("assert"); }}>Verify Enabled</button>
            <button className="cmd-btn cmd-btn--assert" title="Compare two values (variables, strings, numbers) using ==, !=, contains, >, < and more. Supports trim, ignoreCase." onClick={() => { setEvalLeft(""); setEvalOp("=="); setEvalRight(""); setEvalStoreAs(""); setEvalFailIfFalse(false); setEvalTrim(false); setEvalIgnoreCase(false); setEvalParseNumber(false); open("evaluate"); }}>Compare Values</button>
          </>)}
          {activeCategory === "input" && (<>
            <button className="cmd-btn" title="Clear the text of a focused input field." onClick={() => open("clearText")}>Clear Text</button>
            <button className="cmd-btn" title="Dismiss the soft keyboard." onClick={() => { addDirect({ id: generateId(), type: "hideKeyboard" }); }}>Hide Keyboard</button>
          </>)}
          {activeCategory === "wait" && (<>
            <button className="cmd-btn" title="Wait until an element is visible, hidden, enabled, or disabled. Stops as soon as the condition is met." onClick={() => { open("waitFor"); setNumVal(10000); }}>Wait For Element</button>
          </>)}
          {activeCategory === "swipe" && (<>
            <button className="cmd-btn" title="Swipe from any point to any point with a custom duration." onClick={() => { open("swipe"); setNumVal(300); }}>Swipe Custom</button>
            <button className="cmd-btn" title="Keep scrolling in a direction until the target element appears." onClick={() => { open("scrollUntilVisible"); setNumVal(10); }}>Scroll To Element</button>
          </>)}
          {activeCategory === "vars" && (<>
            <button className="cmd-btn cmd-btn--var" title="Set a variable to a literal value or capture it from an element. Reference later as \${varName}." onClick={() => addDirect({ id: generateId(), type: "setVariable", name: "myVar", source: { kind: "literal", value: "" } } as SetVariableStep)}>Set Variable</button>
            <button className="cmd-btn cmd-btn--var" title="Get (print) the current value of a variable. Useful for debugging mid-test." onClick={() => open("getVariable")}>Get Variable</button>
            <button className="cmd-btn cmd-btn--var" title="Generate a random string, number, or alphanumeric value and store it in a variable." onClick={() => open("randomGenerator")}>Random Value</button>
            <button className="cmd-btn cmd-btn--var" title="Read any element attribute (enabled, checked, resourceId, bounds…) into a variable." onClick={() => { setLocatorValue(""); open("getAttribute"); }}>Get Attribute</button>
            <button className="cmd-btn cmd-btn--var" title="Count how many elements match a locator and store the number in a variable." onClick={() => { setLocatorValue(""); setInputVal(""); open("getCount"); }}>Get Count</button>
          </>)}
          {activeCategory === "gesture" && (<>
            <button className="cmd-btn" title="Hold (long-press) an element, then drag in a direction — useful for drag-to-scroll in lists." onClick={() => { setHoldMs(800); setHoldDistance(400); setLocatorValue(""); open("holdSwipe"); }}>Hold &amp; Scroll</button>
          </>)}
          {activeCategory === "report" && (<>
            <button className="cmd-btn cmd-btn--print" title="Log a message to the run report. Use \${varName} to embed values." onClick={() => addDirect({ id: generateId(), type: "print", message: "Step reached", level: "info" })}>Print Log</button>
            <button className="cmd-btn cmd-btn--pass" title="Mark this point in the test as PASSED. Appears in the report as a green checkpoint." onClick={() => addDirect({ id: generateId(), type: "checkpoint", message: "Checkpoint passed", status: "pass" })}>✓ Mark Pass</button>
            <button className="cmd-btn cmd-btn--fail" title="Mark this point in the test as FAILED. Appears in the report as a red checkpoint." onClick={() => addDirect({ id: generateId(), type: "checkpoint", message: "Checkpoint failed", status: "fail" })}>✗ Mark Fail</button>
          </>)}
          {activeCategory === "control" && (<>
            <button className="cmd-btn cmd-btn--stop" title="Halt execution immediately. No further steps will run." onClick={() => addDirect({ id: generateId(), type: "stopTest", message: "Test stopped", status: "failed" })}>⏹ Stop Test</button>
          </>)}
          {activeCategory === "navigate" && (<>
            <button className="cmd-btn" title="Open a URL in the default browser." onClick={() => open("openUrl")}>Open URL</button>
            <button className="cmd-btn" title="Trigger a deep link (e.g. myapp://path). Optionally constrain to a specific app." onClick={() => open("deepLink")}>Deep Link</button>
          </>)}
          {activeCategory === "device" && (<>
            <button className="cmd-btn" title="Enable WiFi." onClick={() => { setWifiVal(true); setAirplaneVal(undefined); open("setDeviceState"); }}>WiFi On</button>
            <button className="cmd-btn" title="Disable WiFi." onClick={() => { setWifiVal(false); setAirplaneVal(undefined); open("setDeviceState"); }}>WiFi Off</button>
            <button className="cmd-btn" title="Enable Airplane Mode — disables all radios. Avoid over WiFi ADB." onClick={() => { setAirplaneVal(true); setWifiVal(undefined); open("setDeviceState"); }}>Airplane On</button>
            <button className="cmd-btn" title="Disable Airplane Mode." onClick={() => { setAirplaneVal(false); setWifiVal(undefined); open("setDeviceState"); }}>Airplane Off</button>
            <button className="cmd-btn" title="Switch the system UI to Dark Theme (Android 10+)." onClick={() => addDirect({ id: generateId(), type: "setDeviceState", darkMode: true })}>Dark Mode On</button>
            <button className="cmd-btn" title="Switch the system UI to Light Theme (Android 10+)." onClick={() => addDirect({ id: generateId(), type: "setDeviceState", darkMode: false })}>Dark Mode Off</button>
            <button className="cmd-btn" title="Enable location services (high accuracy: GPS + network)." onClick={() => addDirect({ id: generateId(), type: "setDeviceState", location: true })}>Location On</button>
            <button className="cmd-btn" title="Disable location services." onClick={() => addDirect({ id: generateId(), type: "setDeviceState", location: false })}>Location Off</button>
            <button className="cmd-btn" title="Show the soft keyboard (IME)." onClick={() => addDirect({ id: generateId(), type: "setDeviceState", showKeyboard: true })}>Show Keyboard</button>
          </>)}
          {activeCategory === "flow" && (<>
            <button className="cmd-btn cmd-btn--flow" title="Add an IF condition — branches test based on element state." onClick={() => addDirect({ id: generateId(), type: "if", condition: { selector: { description: "element", candidates: [] }, assertion: "visible" }, then: [] })}>If Condition</button>
            <button className="cmd-btn cmd-btn--flow" title="Repeat a group of steps N times." onClick={() => addDirect({ id: generateId(), type: "repeat", count: 3, steps: [] })}>Repeat ×N</button>
            <button className="cmd-btn cmd-btn--flow" title="Iterate over rows in a data source." onClick={() => addDirect({ id: generateId(), type: "forEachRow", dataSource: "data", variable: "row", steps: [] })}>For Each Row</button>
            <button className="cmd-btn cmd-btn--loop-ctrl" title="Exit the current loop early." onClick={() => addDirect({ id: generateId(), type: "break" })}>Break</button>
            <button className="cmd-btn cmd-btn--loop-ctrl" title="Skip to the next loop iteration." onClick={() => addDirect({ id: generateId(), type: "continue" })}>Continue</button>
            <button className="cmd-btn cmd-btn--flow" title="Make an HTTP API call. Supports all methods, headers, body, and response capture." onClick={() => addDirect({ id: generateId(), type: "api", method: "GET", url: "https://" })}>API Call</button>
            <button className="cmd-btn cmd-btn--flow" title="Run a JavaScript snippet (sandboxed). Use for custom logic not covered by other commands." onClick={() => addDirect({ id: generateId(), type: "script", code: "// your script here", language: "javascript" })}>Script</button>
            <button className="cmd-btn cmd-btn--var" title="Set a variable." onClick={() => addDirect({ id: generateId(), type: "setVariable", name: "myVar", source: { kind: "literal", value: "" } } as SetVariableStep)}>Set Variable</button>
            <button className="cmd-btn cmd-btn--flow" title="Call a reusable function from the Function Library." onClick={() => { setCfSelectedId(sharedFunctions[0]?.id ?? ""); setCfArgs({}); open("callFunction"); }}>
              ♻ Call Function{sharedFunctions.length > 0 ? ` (${sharedFunctions.length})` : ""}
            </button>
          </>)}
        </div>
      )}

      {/* ═══ Active input form ═══ */}
      {activeForm && (
        <div className="command-form">
          {/* Command title heading */}
          <span className="command-form-title">
            {FORM_TITLES[activeForm.kind] ?? activeForm.kind}
          </span>
          {/* Capture from mirror CTA for element-based commands */}
          {ELEMENT_FORM_KINDS.has(activeForm.kind) && onStartCapture && (
            <div className={`ctb-capture-cta${capturing ? " ctb-capture-cta--active" : ""}`}>
              {capturing ? (
                <>
                  <span className="rec-dot" />
                  <span>Tap any element on the phone mirror…</span>
                  <button className="ctb-capture-cancel" onMouseDown={(e) => { e.preventDefault(); onStopCapture?.(); }}>Cancel</button>
                </>
              ) : (
                <button className="ctb-capture-btn" onMouseDown={(e) => {
                  e.preventDefault();
                  onStartCapture((sel) => {
                    const cand = sel.candidates[0];
                    if (cand) { setLocatorStrategy(cand.strategy as LocatorStrategy); setLocatorValue(cand.value); }
                  });
                }}>
                  📱 Tap element on mirror to capture locator
                </button>
              )}
            </div>
          )}
          {/* Package selector (launchApp / stopApp) */}
          {isPkgForm && (
            <>
              <label className="command-form-label">{activeForm.kind === "launchApp" ? "Open App" : "Close App"}:</label>
              <input className="command-form-input" placeholder={pkgLoading ? "Loading…" : "Search or type package name"}
                value={inputVal} list="mimiq-pkg-list" disabled={pkgLoading}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }}
                autoFocus />
              <datalist id="mimiq-pkg-list">{packages.map((p) => <option key={p} value={p} />)}</datalist>
              {packages.length > 0 && <span className="pkg-count">{packages.length} packages</span>}
            </>
          )}
          {/* Text input (typeText, openUrl, deepLink) */}
          {(activeForm.kind === "typeText" || activeForm.kind === "openUrl" || activeForm.kind === "deepLink") && (
            <>
              <label className="command-form-label">
                {activeForm.kind === "typeText" ? "Text" : "URL"}:
              </label>
              <input className="command-form-input"
                type={activeForm.kind === "typeText" && sensitiveInput ? "password" : "text"}
                placeholder={activeForm.kind === "typeText" ? "Text to type…" : activeForm.kind === "openUrl" ? "https://…" : "myapp://path"}
                value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }}
                autoFocus />
              {activeForm.kind === "typeText" && (
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.75rem", color: sensitiveInput ? "var(--color-warning)" : "var(--color-text-muted)", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={sensitiveInput} onChange={(e) => setSensitiveInput(e.target.checked)} />
                  🔒 Password (mask)
                </label>
              )}
              {activeForm.kind === "deepLink" && (
                <input className="command-form-input" placeholder="Package (optional)" value={input2}
                  onChange={(e) => setInput2(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              )}
            </>
          )}
          {/* Locator form (doubleTap, longPress, clearText, waitFor, scrollUntilVisible, assert) */}
          {(activeForm.kind === "doubleTap" || activeForm.kind === "longPress" || activeForm.kind === "clearText" ||
            activeForm.kind === "waitFor" || activeForm.kind === "scrollUntilVisible" || activeForm.kind === "assert") && (
            <>
              {activeForm.kind === "assert" && (
                <label className="command-form-label">
                  Verify {assertionPreset === "notVisible" ? "hidden" : assertionPreset === "enabled" ? "enabled" : "visible"} —
                </label>
              )}
              <label className="command-form-label">{activeForm.kind === "assert" ? "" : "Find element:"}</label>
              <select className="command-form-select" value={locatorStrategy}
                onChange={(e) => setLocatorStrategy(e.target.value as LocatorStrategy)} autoFocus>
                {LOCATOR_STRATEGIES.map((s) => <option key={s.strategy} value={s.strategy}>{s.label}</option>)}
              </select>
              <input className="command-form-input"
                placeholder={LOCATOR_STRATEGIES.find((s) => s.strategy === locatorStrategy)?.placeholder ?? "value"}
                value={locatorValue}
                onChange={(e) => setLocatorValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
            </>
          )}
          {/* Number input (wait, longPress, swipe durationMs, waitFor timeout, scrollUntilVisible maxScrolls) */}
          {(activeForm.kind === "wait" || activeForm.kind === "longPress" || activeForm.kind === "swipe" ||
            activeForm.kind === "waitFor" || activeForm.kind === "scrollUntilVisible") && (
            <>
              <label className="command-form-label">
                {activeForm.kind === "wait" ? "Duration (ms)" :
                 activeForm.kind === "longPress" ? "Hold duration (ms)" :
                 activeForm.kind === "swipe" ? "Swipe duration (ms)" :
                 activeForm.kind === "waitFor" ? "Timeout (ms)" :
                 "Max scrolls"}:
              </label>
              <input type="number" className="command-form-input command-form-input--short" min="1"
                value={numVal} onChange={(e) => setNumVal(parseInt(e.target.value) || 1)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }}
                autoFocus={activeForm.kind === "wait"} />
            </>
          )}
          {/* Direction picker (swipe, scrollUntilVisible) */}
          {(activeForm.kind === "swipe" || activeForm.kind === "scrollUntilVisible") && (
            <>
              <label className="command-form-label">Direction:</label>
              <select className="command-form-select" value={dirVal} onChange={(e) => setDirVal(e.target.value as typeof dirVal)}>
                <option value="down">Down</option>
                <option value="up">Up</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </>
          )}
          {/* State picker (waitFor) */}
          {activeForm.kind === "waitFor" && (
            <>
              <label className="command-form-label">Wait for:</label>
              <select className="command-form-select" value={stateVal} onChange={(e) => setStateVal(e.target.value as typeof stateVal)}>
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </>
          )}
          {/* Device state display (setDeviceState) */}
          {activeForm.kind === "setDeviceState" && (
            <span className="command-form-label">
              {wifiVal !== undefined ? `WiFi → ${wifiVal ? "ON" : "OFF"}` : ""}
              {airplaneVal !== undefined ? `Airplane → ${airplaneVal ? "ON" : "OFF"}` : ""}
            </span>
          )}
          {/* Call Function */}
          {activeForm.kind === "callFunction" && (
            <>
              {sharedFunctions.length === 0 ? (
                <span className="command-form-label" style={{ color: "var(--color-text-muted)" }}>
                  No functions yet — create one in the ♻ Functions tab first.
                </span>
              ) : (
                <FunctionPicker
                  functions={sharedFunctions}
                  selectedId={cfSelectedId}
                  args={cfArgs}
                  onSelect={(id) => { setCfSelectedId(id); setCfArgs({}); }}
                  onArgChange={(name, val) => setCfArgs(prev => ({ ...prev, [name]: val }))}
                  onConfirm={confirm}
                  onCancel={cancel}
                />
              )}
            </>
          )}
          {/* Compare Values (evaluate) */}
          {activeForm.kind === "evaluate" && (
            <>
              <label className="command-form-label">Left:</label>
              <input className="command-form-input" placeholder='${myVar} or "text"' value={evalLeft}
                onChange={(e) => setEvalLeft(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <select className="command-form-select" value={evalOp} onChange={(e) => setEvalOp(e.target.value)}>
                {OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
              </select>
              {!["isEmpty","isNotEmpty"].includes(evalOp) && (
                <input className="command-form-input" placeholder='${other} or "value"' value={evalRight}
                  onChange={(e) => setEvalRight(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              )}
              <label className="command-form-label">→ var:</label>
              <input className="command-form-input command-form-input--short" placeholder="result (opt)" value={evalStoreAs}
                onChange={(e) => setEvalStoreAs(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <label style={{ display:"flex", gap:3, alignItems:"center", fontSize:"0.72rem", color:"var(--color-text-muted)", cursor:"pointer", whiteSpace:"nowrap" }}>
                <input type="checkbox" checked={evalFailIfFalse} onChange={(e) => setEvalFailIfFalse(e.target.checked)} /> Fail if false
              </label>
              <label style={{ display:"flex", gap:3, alignItems:"center", fontSize:"0.72rem", color:"var(--color-text-muted)", cursor:"pointer", whiteSpace:"nowrap" }}>
                <input type="checkbox" checked={evalTrim} onChange={(e) => setEvalTrim(e.target.checked)} /> Trim
              </label>
              <label style={{ display:"flex", gap:3, alignItems:"center", fontSize:"0.72rem", color:"var(--color-text-muted)", cursor:"pointer", whiteSpace:"nowrap" }}>
                <input type="checkbox" checked={evalIgnoreCase} onChange={(e) => setEvalIgnoreCase(e.target.checked)} /> Ignore case
              </label>
            </>
          )}
          {/* Get Count */}
          {activeForm.kind === "getCount" && (
            <>
              <label className="command-form-label">Find element:</label>
              <select className="command-form-select" value={locatorStrategy}
                onChange={(e) => setLocatorStrategy(e.target.value as LocatorStrategy)} autoFocus>
                {LOCATOR_STRATEGIES.map((s) => <option key={s.strategy} value={s.strategy}>{s.label}</option>)}
              </select>
              <input className="command-form-input"
                placeholder={LOCATOR_STRATEGIES.find((s) => s.strategy === locatorStrategy)?.placeholder ?? "value"}
                value={locatorValue} onChange={(e) => setLocatorValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <label className="command-form-label">Store count in:</label>
              <input className="command-form-input command-form-input--short" placeholder="elementCount" value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
            </>
          )}
          {/* Get Attribute */}
          {activeForm.kind === "getAttribute" && (
            <>
              <label className="command-form-label">Find element:</label>
              <select className="command-form-select" value={locatorStrategy}
                onChange={(e) => setLocatorStrategy(e.target.value as LocatorStrategy)} autoFocus>
                {LOCATOR_STRATEGIES.map((s) => <option key={s.strategy} value={s.strategy}>{s.label}</option>)}
              </select>
              <input className="command-form-input"
                placeholder={LOCATOR_STRATEGIES.find((s) => s.strategy === locatorStrategy)?.placeholder ?? "value"}
                value={locatorValue} onChange={(e) => setLocatorValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <label className="command-form-label">Attribute:</label>
              <select className="command-form-select" value={attrAttr} onChange={(e) => setAttrAttr(e.target.value)}>
                {ATTR_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <label className="command-form-label">Store in:</label>
              <input className="command-form-input command-form-input--short" placeholder="myVar" value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
            </>
          )}
          {/* Get Variable */}
          {activeForm.kind === "getVariable" && (
            <>
              <label className="command-form-label">Variable name:</label>
              <input className="command-form-input" placeholder="myVar" value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }}
                autoFocus />
              {inputVal.trim() && (
                <span className="command-form-label" style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                  Adds: print &ldquo;{inputVal.trim()} = ${"{" + inputVal.trim() + "}"}&rdquo;
                </span>
              )}
            </>
          )}
          {/* Random Generator */}
          {activeForm.kind === "randomGenerator" && (
            <>
              <label className="command-form-label">Store in variable:</label>
              <input className="command-form-input" placeholder="randomValue" value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }}
                autoFocus />
              <label className="command-form-label">Type:</label>
              <select className="command-form-select" value={randomType} onChange={(e) => setRandomType(e.target.value as typeof randomType)}>
                <option value="alphanumeric">Alphanumeric (e.g. aB3xZ9)</option>
                <option value="string">Letters only (e.g. qxkrtz)</option>
                <option value="number">Number (e.g. 4821)</option>
              </select>
              {randomType !== "number" ? (
                <>
                  <label className="command-form-label">Length:</label>
                  <input type="number" className="command-form-input command-form-input--short" min="1" max="64"
                    value={randomLength} onChange={(e) => setRandomLength(parseInt(e.target.value) || 8)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
                </>
              ) : (
                <>
                  <label className="command-form-label">Min:</label>
                  <input type="number" className="command-form-input command-form-input--short"
                    value={randomMin} onChange={(e) => setRandomMin(parseInt(e.target.value) || 0)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
                  <label className="command-form-label">Max:</label>
                  <input type="number" className="command-form-input command-form-input--short"
                    value={randomMax} onChange={(e) => setRandomMax(parseInt(e.target.value) || 999999)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
                </>
              )}
            </>
          )}
          {/* Hold & Scroll */}
          {activeForm.kind === "holdSwipe" && (
            <>
              <label className="command-form-label">Find element:</label>
              <select className="command-form-select" value={locatorStrategy}
                onChange={(e) => setLocatorStrategy(e.target.value as LocatorStrategy)} autoFocus>
                {LOCATOR_STRATEGIES.map((s) => <option key={s.strategy} value={s.strategy}>{s.label}</option>)}
              </select>
              <input className="command-form-input"
                placeholder={LOCATOR_STRATEGIES.find((s) => s.strategy === locatorStrategy)?.placeholder ?? "value"}
                value={locatorValue} onChange={(e) => setLocatorValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <label className="command-form-label">Scroll:</label>
              <select className="command-form-select" value={dirVal} onChange={(e) => setDirVal(e.target.value as typeof dirVal)}>
                <option value="up">Up</option>
                <option value="down">Down</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
              <label className="command-form-label">Hold (ms):</label>
              <input type="number" className="command-form-input command-form-input--short" placeholder="800"
                value={holdMs}
                onChange={(e) => setHoldMs(e.target.value === "" ? "" : parseInt(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
              <label className="command-form-label">Distance (px):</label>
              <input type="number" className="command-form-input command-form-input--short" placeholder="400"
                value={holdDistance}
                onChange={(e) => setHoldDistance(e.target.value === "" ? "" : parseInt(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirm(); } if (e.key === "Escape") cancel(); }} />
            </>
          )}
          <button className="cmd-btn cmd-btn--confirm" onClick={confirm}>Add</button>
          <button className="cmd-btn cmd-btn--cancel" onClick={cancel}>Cancel</button>
        </div>
      )}
    </div>
  );
}

/* ── FunctionPicker — searchable combobox for 100+ functions ─────────────── */

interface FunctionPickerProps {
  functions: import("./db/schema.js").SharedFlow[];
  selectedId: string;
  args: Record<string, string>;
  onSelect: (id: string) => void;
  onArgChange: (name: string, val: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function FunctionPicker({ functions, selectedId, args, onSelect, onArgChange, onConfirm, onCancel }: FunctionPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);

  const filtered = query.trim()
    ? functions.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()))
    : functions;

  const selected = functions.find((f) => f.id === selectedId);

  function pick(id: string) {
    onSelect(id);
    setQuery(functions.find(f => f.id === id)?.name ?? "");
    setOpen(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 260, width: "100%", position: "relative" }}>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <label className="command-form-label">Function:</label>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            className="command-form-input"
            placeholder="Search functions…"
            value={query}
            autoFocus
            onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onSelect(""); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered.length === 1) { pick(filtered[0]!.id); }
              if (e.key === "Escape") { setOpen(false); onCancel(); }
            }}
          />
          {open && filtered.length > 0 && (
            <ul className="fn-picker-dropdown">
              {filtered.slice(0, 12).map((f) => (
                <li key={f.id}>
                  <button className={`fn-picker-option${selectedId === f.id ? " fn-picker-option--active" : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(f.id); }}>
                    <span className="fn-picker-name">♻ {f.name}</span>
                    <span className="fn-picker-meta">{f.steps.length}s · {f.parameters.length}p</span>
                  </button>
                </li>
              ))}
              {filtered.length > 12 && (
                <li className="fn-picker-more">…{filtered.length - 12} more — keep typing to filter</li>
              )}
            </ul>
          )}
        </div>
      </div>
      {selected && selected.parameters.length > 0 && (
        <>
          <span className="command-form-label" style={{ width: "100%" }}>Arguments:</span>
          {selected.parameters.map((p) => (
            <div key={p.name} style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <label className="command-form-label" style={{ minWidth: 80 }}>{p.name}:</label>
              <input className="command-form-input"
                placeholder={p.defaultValue ? `default: "${p.defaultValue}"` : `\${${p.name}}`}
                value={args[p.name] ?? ""}
                onChange={(e) => onArgChange(p.name, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(); } if (e.key === "Escape") onCancel(); }} />
            </div>
          ))}
        </>
      )}
      {!selected && query.trim() && filtered.length === 0 && (
        <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>No functions match "{query}"</span>
      )}
    </div>
  );
}

/* ── RecorderPanel ────────────────────────────────────────────────────────── */

interface RecorderPanelProps {
  adb: Adb;
  recording: boolean;
  replacingIndex: number | null;
  steps: readonly IRStep[];
  onStartRecording: () => void;
  onStopRecording: () => void;
  onClearSteps: () => void;
  onAddStep: (step: IRStep) => void;
  onDeleteStep: (i: number) => void;
  onReRecordStep: (i: number) => void;
  onReorderCandidate: (si: number, ci: number) => void;
  onRemoveCandidate: (si: number, ci: number) => void;
  onMoveStep: (i: number, direction: "up" | "down") => void;
  onUpdateStep: (i: number, step: IRStep) => void;
  onExportIR: () => void;
  onLoadTestCase: () => void;
  onUpdateCandidate: (si: number, ci: number, updates: Partial<Pick<import("@mimiq/ir").Candidate, "strategy" | "value" | "index">>) => void;
  onAddCandidate: (si: number, candidate: import("@mimiq/ir").Candidate) => void;
  onReorderStepsByIds: (orderedIds: string[]) => void;
  onSaveTest: () => void;
  onNewTest: () => void;
  /** When editing a function's steps, the function id and name are set */
  activeFunctionId: string | null;
  activeFunctionName: string;
  onSaveToFunction: () => void;
  onCancelFunctionEdit: () => void;
  showQuickAdd: boolean;
  onShowQuickAdd: (show: boolean) => void;
  onStartCapture: (callback: (sel: Selector) => void) => void;
  onStopCapture: () => void;
  capturing: boolean;
  activeTestCase: TestCase | null;
  canvasMode: CanvasMode;
  onSwitchToInspect: () => void;
  onSwitchToDrive: () => void;
  onSwitchToAssist: () => void;
  deviceWidth?: number;
  deviceHeight?: number;
  onAddNestedStep: (parentId: string, branch: "then" | "else" | "steps", step: IRStep) => void;
  onDeleteNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number) => void;
  onMoveNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number, direction: "up" | "down") => void;
  onUpdateNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number, step: IRStep) => void;
}

function RecorderPanel({
  adb,
  recording,
  replacingIndex,
  steps,
  onStartRecording,
  onStopRecording,
  onClearSteps,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAddStep,
  onDeleteStep,
  onReRecordStep,
  onReorderCandidate,
  onRemoveCandidate,
  onMoveStep,
  onUpdateStep,
  onExportIR,
  onLoadTestCase,
  onUpdateCandidate,
  onAddCandidate,
  onReorderStepsByIds,
  onSaveTest,
  onNewTest,
  activeFunctionId,
  activeFunctionName,
  onSaveToFunction,
  onCancelFunctionEdit,
  showQuickAdd,
  onShowQuickAdd,
  onStartCapture,
  onStopCapture,
  capturing,
  activeTestCase,
  canvasMode,
  onSwitchToInspect,
  onSwitchToDrive,
  onSwitchToAssist,
  deviceWidth,
  deviceHeight,
  onAddNestedStep,
  onDeleteNestedStep,
  onMoveNestedStep,
  onUpdateNestedStep,
}: RecorderPanelProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // dnd-kit sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function toggleExpand(i: number) {
    setExpandedIndex((prev) => (prev === i ? null : i));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    // Reorder: move oldIndex to newIndex
    const ids = steps.map((s) => s.id);
    const [moved] = ids.splice(oldIndex, 1);
    ids.splice(newIndex, 0, moved!);
    onReorderStepsByIds(ids);
    setExpandedIndex(null);
  }

  return (
    <section className="recorder-panel">
      {/* ── Function editing banner (replaces normal save flow) ── */}
      {activeFunctionId && (
        <div className="fn-edit-banner">
          <span className="fn-edit-banner-icon">♻</span>
          <span className="fn-edit-banner-text">Editing function: <strong>{activeFunctionName}</strong></span>
          <button className="btn btn--primary fn-edit-save" onClick={onSaveToFunction}
            disabled={steps.length === 0}
            title={steps.length === 0 ? "Add at least one step" : "Save these steps back to the function"}>
            ✓ Save to Function
          </button>
          <button className="btn btn--ghost fn-edit-cancel" onClick={onCancelFunctionEdit} title="Discard changes and leave function edit mode">
            ✕ Cancel
          </button>
        </div>
      )}

      {/* ── Title row ── */}
      <div className="recorder-title-row">
        <strong>Recorder</strong>
        <span className="recorder-count">
          {steps.length} step{steps.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Big centered Drive / Inspect / Assist toggle ── */}
      <div className="recorder-mode-toggle">
        <button
          className={`recorder-mode-btn${canvasMode === "drive" ? " recorder-mode-btn--drive" : ""}`}
          onClick={onSwitchToDrive}
          title="Drive mode — taps are forwarded to phone and recorded as steps"
        >
          ⏺ Drive
        </button>
        <button
          className={`recorder-mode-btn${canvasMode === "inspect" ? " recorder-mode-btn--inspect" : ""}`}
          onClick={onSwitchToInspect}
          title="Inspect mode — click any element to see attributes and add assertions (Alt)"
        >
          🔍 Inspect
        </button>
        {getAiAssistEnabled() && (
          <button
            className={`recorder-mode-btn${canvasMode === "assist" ? " recorder-mode-btn--assist" : ""}`}
            onClick={onSwitchToAssist}
            title="AI Assist — describe a step in plain English"
          >
            ✨ Assist
          </button>
        )}
      </div>

      {/* Inspect mode banner */}
      {canvasMode === "inspect" && (
        <div className="mode-banner mode-banner--inspect">
          Click any element on the mirror to see its attributes. Switch to <strong>Drive</strong> to record steps.
        </div>
      )}

      {/* AI Assist bar */}
      {canvasMode === "assist" && (
        <AssistBar
          adb={adb}
          deviceWidth={deviceWidth}
          deviceHeight={deviceHeight}
          onAddSteps={(newSteps) => newSteps.forEach(onAddStep)}
          onStartCapture={onStartCapture}
          onStopCapture={onStopCapture}
        />
      )}

      {/* ── Action buttons row ── */}
      <div className="recorder-actions-row">
        <button
          className="btn btn--new-test"
          onClick={onNewTest}
          disabled={recording}
          title="Clear steps and start a new test"
        >
          + New Test
        </button>

        {canvasMode === "drive" && (
          recording ? (
            <button className="btn btn--danger" onClick={onStopRecording}>
              <span className="rec-dot" />
              {replacingIndex !== null ? "Cancel Re-record" : "Stop Recording"}
            </button>
          ) : (
            <button className="btn btn--primary" onClick={onStartRecording}>
              ▶ Start Recording
            </button>
          )
        )}

        {steps.length > 0 && !recording && (
          <button className="btn btn--warning" onClick={onClearSteps} title="Clear all steps">Clear</button>
        )}
        {steps.length > 0 && !recording && !activeFunctionId && (
          <button className="btn btn--save" onClick={onSaveTest}
            title={activeTestCase ? "Save changes to this test" : "Save test to library"}>
            {activeTestCase ? "Save" : "Save As"}
          </button>
        )}
        <button
          className="btn btn--load-test"
          onClick={onLoadTestCase}
          disabled={recording}
          title="Load a test case from a JSON or ZIP file"
        >
          Load
        </button>
        {steps.length > 0 && !recording && (
          <button className="btn btn--export" onClick={onExportIR} title="Download test as JSON file">
            ↓ Download
          </button>
        )}
      </div>

      <div className="custom-commands-header">
        <span className="custom-commands-label">Custom Commands</span>
      </div>
      <CommandToolbar
        adb={adb} onAddStep={onAddStep}
        deviceWidth={deviceWidth} deviceHeight={deviceHeight}
        onStartCapture={onStartCapture}
        onStopCapture={onStopCapture}
        capturing={capturing}
      />

      {/* Re-record mode banner */}
      {recording && replacingIndex !== null && (
        <div className="rerecord-banner">
          <span className="rerecord-dot" />
          Re-recording step {replacingIndex + 1} — tap the element on the phone mirror
        </div>
      )}

      {/* Regular recording hint */}
      {recording && replacingIndex === null && (
        <p className="recorder-hint">
          Click on the phone mirror to record tap steps. Use the commands above for app launch, keys, waits, and text input.
        </p>
      )}

      {/* Capture mode banner */}
      {capturing && (
        <div className="capture-mode-banner">
          <span className="capture-mode-dot" />
          Click an element on the phone mirror to select it
          <button className="capture-mode-cancel" onClick={onStopCapture}>Cancel (Esc)</button>
        </div>
      )}

      {steps.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ol className="step-list">
              {steps.map((step, i) => (
                <SortableStepItem
                  key={step.id}
                  step={step}
                  index={i}
                  isExpanded={expandedIndex === i}
                  recording={recording}
                  onToggleExpand={() => toggleExpand(i)}
                  onReRecordStep={() => onReRecordStep(i)}
                  onDeleteStep={() => { if (expandedIndex === i) setExpandedIndex(null); onDeleteStep(i); }}
                  onReorderCandidate={onReorderCandidate}
                  onRemoveCandidate={onRemoveCandidate}
                  onUpdateCandidate={onUpdateCandidate}
                  onAddCandidate={onAddCandidate}
                  onUpdateStep={(updated) => { onUpdateStep(i, updated); setExpandedIndex(null); }}
                  onCancelEdit={() => setExpandedIndex(null)}
                  onAddNestedStep={onAddNestedStep}
                  onDeleteNestedStep={onDeleteNestedStep}
                  onMoveNestedStep={onMoveNestedStep}
                  onUpdateNestedStep={onUpdateNestedStep}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      )}

      {/* Quick Add Step — inline step creation row */}
      {showQuickAdd ? (
        <QuickAddStep
          onAdd={(step) => { onAddStep(step); onShowQuickAdd(false); }}
          onCancel={() => { onStopCapture(); onShowQuickAdd(false); }}
          onStartCapture={onStartCapture}
          onStopCapture={onStopCapture}
          capturing={capturing}
          adb={adb}
        />
      ) : (
        <button
          className="btn--add-step-trigger"
          onClick={() => onShowQuickAdd(true)}
          title="Add a step by typing a command (works during recording too)"
        >
          + Add Step
        </button>
      )}
    </section>
  );
}

/* ── SortableStepItem ─────────────────────────────────────────────────────── */

interface SortableStepItemProps {
  step: IRStep;
  index: number;
  isExpanded: boolean;
  onAddNestedStep: (parentId: string, branch: "then" | "else" | "steps", step: IRStep) => void;
  onDeleteNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number) => void;
  onMoveNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number, direction: "up" | "down") => void;
  onUpdateNestedStep: (parentId: string, branch: "then" | "else" | "steps", index: number, step: IRStep) => void;
  recording: boolean;
  onToggleExpand: () => void;
  onReRecordStep: () => void;
  onDeleteStep: () => void;
  onReorderCandidate: (si: number, ci: number) => void;
  onRemoveCandidate: (si: number, ci: number) => void;
  onUpdateCandidate: (si: number, ci: number, updates: Partial<Pick<import("@mimiq/ir").Candidate, "strategy" | "value" | "index">>) => void;
  onAddCandidate: (si: number, candidate: import("@mimiq/ir").Candidate) => void;
  onUpdateStep: (updated: IRStep) => void;
  onCancelEdit: () => void;
}

function SortableStepItem({
  step, index, isExpanded, recording,
  onToggleExpand, onReRecordStep, onDeleteStep,
  onReorderCandidate, onRemoveCandidate, onUpdateCandidate, onAddCandidate,
  onUpdateStep, onCancelEdit,
  onAddNestedStep, onDeleteNestedStep, onMoveNestedStep, onUpdateNestedStep,
}: SortableStepItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : undefined,
  };

  const isTap = step.type === "tap";
  const firstStrategy = stepFirstStrategy(step);
  const conf = strategyConfidence(firstStrategy);

  return (
    <li ref={setNodeRef} style={style} className={`step-item${isExpanded ? " step-item--expanded" : ""}${isDragging ? " step-item--dragging" : ""}`}>
      <div
        className="step-row"
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => e.key === "Enter" && onToggleExpand()}
      >
        {/* Drag handle */}
        <span
          className="step-drag-handle"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          title="Drag to reorder"
        >⋮⋮</span>
        <span className="step-num">{index + 1}</span>
        <span className="step-type">{step.type}</span>
        <span className="step-desc">{stepDescription(step)}</span>
        {(step.type === "assert" || step.type === "waitFor" || step.type === "clearText" ||
          step.type === "doubleTap" || step.type === "longPress" || step.type === "scrollUntilVisible") &&
          step.selector.candidates.length === 0 && (
          <span className="step-no-locator-warn" title="No element locator set — will fail at replay. Click to edit.">⚠ no locator</span>
        )}
        {isTap && (
          <span className={`strategy-badge strategy-badge--${conf}`}>{firstStrategy}</span>
        )}
        {/* Coord-only warning — shown when tap has no a11y locators at all */}
        {isTap && (step as TapStep).selector.candidates.length === 0 && (
          <span
            className="step-coord-warn"
            title="No element locator found — this step uses screen coordinates only and will break if the layout changes. Expand this step and use 'Try First' to add a stable locator."
          >
            ⚠ coord only — fragile, click ▸ to fix
          </span>
        )}
        <span className="step-expand-icon">{isExpanded ? "▾" : "▸"}</span>

        <div className="step-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {isTap && (
            <button className="step-action-btn step-action-btn--rerecord" title="Re-record" onClick={onReRecordStep} disabled={recording}>↺</button>
          )}
          <button className="step-action-btn step-action-btn--delete" title="Delete" onClick={onDeleteStep}>✕</button>
        </div>
      </div>

      {isExpanded && isTap && (
        <>
          {/* Step label editor — lets users rename a tap step independent of its locator */}
          <div className="step-label-editor">
            <label className="step-label-editor__lbl">Step label:</label>
            <input
              key={step.id}
              className="step-label-editor__input"
              placeholder="e.g. Tap Login button (optional)"
              defaultValue={(step as TapStep).selector.description.startsWith("coord(") ? "" : (step as TapStep).selector.description}
              onBlur={(e) => {
                const val = e.target.value.trim();
                const tapStep = step as TapStep;
                const newDesc = val || tapStep.selector.description;
                if (newDesc !== tapStep.selector.description) {
                  onUpdateStep({ ...tapStep, selector: { ...tapStep.selector, description: newDesc } });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") {
                  const tapStep = step as TapStep;
                  (e.target as HTMLInputElement).value = tapStep.selector.description.startsWith("coord(") ? "" : tapStep.selector.description;
                }
              }}
            />
          </div>
          <CandidateInspector
            step={step as TapStep}
            stepIndex={index}
            onReorderCandidate={onReorderCandidate}
            onRemoveCandidate={onRemoveCandidate}
            onUpdateCandidate={onUpdateCandidate}
            onAddCandidate={onAddCandidate}
          />
        </>
      )}
      {isExpanded && !isTap && (
        <StepEditForm step={step} onSave={onUpdateStep} onCancel={onCancelEdit} />
      )}
      {/* Nested step lists for control flow steps — always visible (not gated on isExpanded) */}
      {(step.type === "if" || step.type === "repeat" || step.type === "forEachRow") && (
        <NestedStepList
          parentStep={step}
          onAddNestedStep={onAddNestedStep}
          onDeleteNestedStep={onDeleteNestedStep}
          onMoveNestedStep={onMoveNestedStep}
          onUpdateNestedStep={onUpdateNestedStep}
        />
      )}
    </li>
  );
}

/* ── NestedStepList ───────────────────────────────────────────────────────── */

type NestedBranch = "then" | "else" | "steps";

export const NESTED_STEP_TYPES: { type: string; label: string }[] = [
  { type: "tap",               label: "Tap" },
  { type: "doubleTap",         label: "Double Tap" },
  { type: "longPress",         label: "Long Press" },
  { type: "typeText",          label: "Type Text" },
  { type: "clearText",         label: "Clear Text" },
  { type: "wait",              label: "Wait" },
  { type: "waitFor",           label: "Wait For" },
  { type: "pressKey",          label: "Press Key" },
  { type: "screenshot",        label: "Screenshot" },
  { type: "assert",            label: "Assert" },
  { type: "scrollUntilVisible",label: "Scroll To" },
  { type: "print",             label: "Print" },
  { type: "setVariable",       label: "Set Variable" },
  { type: "checkpoint",        label: "Mark Pass/Fail" },
  { type: "openUrl",           label: "Open URL" },
  { type: "launchApp",         label: "Launch App" },
  { type: "swipe",             label: "Swipe ↓" },
];

export function makeDefaultNestedStep(type: string): IRStep {
  const id = generateId();
  switch (type) {
    case "tap":               return { id, type: "tap",        selector: { description: "element", candidates: [] } };
    case "doubleTap":         return { id, type: "doubleTap",  selector: { description: "element", candidates: [] } };
    case "longPress":         return { id, type: "longPress",  selector: { description: "element", candidates: [] }, durationMs: 800 };
    case "clearText":         return { id, type: "clearText",  selector: { description: "element", candidates: [] } };
    case "typeText":          return { id, type: "typeText",   text: "" };
    case "wait":              return { id, type: "wait",       durationMs: 1000 };
    case "waitFor":           return { id, type: "waitFor",    selector: { description: "element", candidates: [] }, state: "visible", timeoutMs: 10000 };
    case "pressKey":          return { id, type: "pressKey",   key: "back" };
    case "screenshot":        return { id, type: "screenshot" };
    case "assert":            return { id, type: "assert",     selector: { description: "element", candidates: [] }, assertion: "visible" };
    case "scrollUntilVisible":return { id, type: "scrollUntilVisible", selector: { description: "element", candidates: [] }, direction: "down", maxScrolls: 10 };
    case "print":             return { id, type: "print",      message: "Step reached", level: "info" };
    case "setVariable":       return { id, type: "setVariable", name: "myVar", source: { kind: "literal", value: "" } } as SetVariableStep;
    case "checkpoint":        return { id, type: "checkpoint", message: "Checkpoint", status: "pass" };
    case "openUrl":           return { id, type: "openUrl",    url: "https://" };
    case "launchApp":         return { id, type: "launchApp",  packageName: "" };
    case "swipe":             return { id, type: "swipe",      from: [540, 1600], to: [540, 800], durationMs: 300 };
    default:                  return { id, type: "wait",       durationMs: 1000 } as IRStep;
  }
}

interface NestedStepListProps {
  parentStep: IRStep;
  onAddNestedStep: (parentId: string, branch: NestedBranch, step: IRStep) => void;
  onDeleteNestedStep: (parentId: string, branch: NestedBranch, index: number) => void;
  onMoveNestedStep: (parentId: string, branch: NestedBranch, index: number, dir: "up" | "down") => void;
  onUpdateNestedStep: (parentId: string, branch: NestedBranch, index: number, step: IRStep) => void;
}

function NestedBranchPanel({
  parentId, branch, label, steps,
  onAdd, onDelete, onMove, onUpdate,
}: {
  parentId: string;
  branch: NestedBranch;
  label: string;
  steps: IRStep[];
  onAdd: (b: NestedBranch, s: IRStep) => void;
  onDelete: (b: NestedBranch, i: number) => void;
  onMove: (b: NestedBranch, i: number, d: "up" | "down") => void;
  onUpdate: (b: NestedBranch, i: number, s: IRStep) => void;
}) {
  void parentId;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  return (
    <div className="nested-branch">
      <div className="nested-branch-header">
        <span className="nested-branch-label">{label}</span>
        <span className="nested-branch-count">{steps.length} step{steps.length !== 1 ? "s" : ""}</span>
      </div>

      {steps.length > 0 && (
        <ol className="nested-step-list">
          {steps.map((step, i) => (
            <li key={step.id} className="nested-step-item">
              <div className="nested-step-row">
                <span className="nested-step-num">{i + 1}</span>
                <span className="nested-step-type">{step.type}</span>
                <span className="nested-step-desc">{stepDescription(step)}</span>
                <div className="nested-step-actions">
                  <button className="nsa-btn" title="Edit" onClick={() => setEditingIdx(editingIdx === i ? null : i)}>✎</button>
                  <button className="nsa-btn" title="Move up" disabled={i === 0} onClick={() => onMove(branch, i, "up")}>↑</button>
                  <button className="nsa-btn" title="Move down" disabled={i === steps.length - 1} onClick={() => onMove(branch, i, "down")}>↓</button>
                  <button className="nsa-btn nsa-btn--delete" title="Delete" onClick={() => { if (editingIdx === i) setEditingIdx(null); onDelete(branch, i); }}>✕</button>
                </div>
              </div>
              {editingIdx === i && (
                <div className="nested-step-edit">
                  <StepEditForm step={step} onSave={(updated) => { onUpdate(branch, i, updated); setEditingIdx(null); }} onCancel={() => setEditingIdx(null)} />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {steps.length === 0 && <p className="nested-empty">No steps yet. Add one below.</p>}

      {pickerOpen ? (
        <div className="nested-picker">
          {NESTED_STEP_TYPES.map(({ type, label: lbl }) => (
            <button key={type} className="nested-picker-btn" onClick={() => {
              onAdd(branch, makeDefaultNestedStep(type));
              setPickerOpen(false);
            }}>{lbl}</button>
          ))}
          <button className="nested-picker-btn nested-picker-btn--cancel" onClick={() => setPickerOpen(false)}>Cancel</button>
        </div>
      ) : (
        <button className="nested-add-btn" onClick={() => setPickerOpen(true)}>+ Add step</button>
      )}
    </div>
  );
}

function NestedStepList({ parentStep, onAddNestedStep, onDeleteNestedStep, onMoveNestedStep, onUpdateNestedStep }: NestedStepListProps) {
  const pid = parentStep.id;
  const add    = (b: NestedBranch, s: IRStep) => onAddNestedStep(pid, b, s);
  const del    = (b: NestedBranch, i: number) => onDeleteNestedStep(pid, b, i);
  const move   = (b: NestedBranch, i: number, d: "up" | "down") => onMoveNestedStep(pid, b, i, d);
  const update = (b: NestedBranch, i: number, s: IRStep) => onUpdateNestedStep(pid, b, i, s);

  if (parentStep.type === "if") {
    return (
      <div className="nested-container">
        <NestedBranchPanel parentId={pid} branch="then" label="Then" steps={parentStep.then as IRStep[]} onAdd={add} onDelete={del} onMove={move} onUpdate={update} />
        <NestedBranchPanel parentId={pid} branch="else" label="Else" steps={(parentStep.else ?? []) as IRStep[]} onAdd={add} onDelete={del} onMove={move} onUpdate={update} />
      </div>
    );
  }
  if (parentStep.type === "repeat") {
    return (
      <div className="nested-container">
        <NestedBranchPanel parentId={pid} branch="steps" label={`Repeat ×${parentStep.count}`} steps={parentStep.steps as IRStep[]} onAdd={add} onDelete={del} onMove={move} onUpdate={update} />
      </div>
    );
  }
  if (parentStep.type === "forEachRow") {
    return (
      <div className="nested-container">
        <NestedBranchPanel parentId={pid} branch="steps" label={`For each ${parentStep.variable}`} steps={parentStep.steps as IRStep[]} onAdd={add} onDelete={del} onMove={move} onUpdate={update} />
      </div>
    );
  }
  return null;
}

/* ── CandidateInspector ───────────────────────────────────────────────────── */

const STRATEGY_OPTIONS = [
  "a11yId", "contentDesc", "text", "textContains", "textRegex",
  "relative", "xpath", "css", "iosPredicate",
] as const;

type EditableStrategy = typeof STRATEGY_OPTIONS[number];

interface CandidateInspectorProps {
  step: TapStep;
  stepIndex: number;
  onReorderCandidate: (si: number, ci: number) => void;
  onRemoveCandidate: (si: number, ci: number) => void;
  onUpdateCandidate: (si: number, ci: number, updates: Partial<Pick<import("@mimiq/ir").Candidate, "strategy" | "value" | "index">>) => void;
  onAddCandidate: (si: number, candidate: import("@mimiq/ir").Candidate) => void;
}

function CandidateInspector({
  step,
  stepIndex,
  onReorderCandidate,
  onRemoveCandidate,
  onUpdateCandidate,
  onAddCandidate,
}: CandidateInspectorProps) {
  const { candidates } = step.selector;
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editStrategy, setEditStrategy] = useState<EditableStrategy>("a11yId");
  const [editValue, setEditValue] = useState("");
  const [editIndexVal, setEditIndexVal] = useState<number>(0);
  const [addingNew, setAddingNew] = useState(false);
  const [newStrategy, setNewStrategy] = useState<EditableStrategy>("contentDesc");
  const [newValue, setNewValue] = useState("");

  function startEdit(ci: number) {
    const c = candidates[ci]!;
    setEditStrategy((STRATEGY_OPTIONS.includes(c.strategy as EditableStrategy) ? c.strategy : "a11yId") as EditableStrategy);
    setEditValue(c.value);
    setEditIndexVal(c.index ?? 0);
    setEditingIndex(ci);
    setAddingNew(false);
  }

  function saveEdit(ci: number) {
    if (!editValue.trim()) return;
    onUpdateCandidate(stepIndex, ci, {
      strategy: editStrategy,
      value: editValue.trim(),
      ...(editIndexVal > 0 ? { index: editIndexVal } : { index: undefined }),
    });
    setEditingIndex(null);
  }

  function saveNew() {
    if (!newValue.trim()) return;
    onAddCandidate(stepIndex, { strategy: newStrategy, value: newValue.trim(), addedBy: "user" });
    setNewValue("");
    setAddingNew(false);
  }

  return (
    <div className="candidate-inspector">
      {candidates.length === 0 ? (
        <p className="ci-empty">No selector candidates — replay uses coordinate fallback.</p>
      ) : (
        candidates.map((c, ci) => (
          <div key={ci} className="ci-row">
            {editingIndex === ci ? (
              /* ── inline edit form ── */
              <div className="ci-edit-form ci-edit-form--expanded">
                <div className="ci-edit-row">
                  <select
                    className="ci-edit-select"
                    value={editStrategy}
                    onChange={(e) => setEditStrategy(e.target.value as EditableStrategy)}
                  >
                    {STRATEGY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input
                    className="ci-edit-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(ci); if (e.key === "Escape") setEditingIndex(null); }}
                    autoFocus
                    placeholder="value"
                  />
                </div>
                <div className="ci-edit-row ci-edit-row--index">
                  <label className="ci-index-label" title="If multiple elements share this locator value, set which one to use (0 = first, 1 = second, 2 = third…)">
                    Match #:
                  </label>
                  <input
                    type="number"
                    className="ci-edit-input ci-edit-input--index"
                    min="0"
                    max="99"
                    value={editIndexVal}
                    onChange={(e) => setEditIndexVal(Math.max(0, parseInt(e.target.value) || 0))}
                    title="0 = first match, 1 = second match, etc. Use this when multiple elements have the same locator (e.g. list rows)"
                  />
                  {editIndexVal > 0 && (
                    <span className="ci-index-hint">→ selects the {["1st","2nd","3rd"][editIndexVal] ?? `${editIndexVal+1}th`} matching element</span>
                  )}
                </div>
                <div className="ci-edit-row">
                  <button className="ci-btn ci-btn--save" onClick={() => saveEdit(ci)}>Save</button>
                  <button className="ci-btn" onClick={() => setEditingIndex(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              /* ── read view ── */
              <>
                <span className={`ci-badge ci-badge--${strategyConfidence(c.strategy)}`}>
                  {c.strategy}
                </span>
                <span className="ci-value" title={c.value}>{c.value}</span>
                {(c.index ?? 0) > 0 && (
                  <span className="ci-index-badge" title={`Selects match #${c.index} (0-based)`}>
                    #{c.index}
                  </span>
                )}
                <div className="ci-actions">
                  <button className="ci-btn" title="Edit this candidate" onClick={() => startEdit(ci)}>Edit</button>
                  {ci > 0 && (
                    <button className="ci-btn" title="Try this candidate first" onClick={() => onReorderCandidate(stepIndex, ci)}>↑ Try first</button>
                  )}
                  {candidates.length > 1 && (
                    <button className="ci-btn ci-btn--remove" title="Remove this candidate" onClick={() => onRemoveCandidate(stepIndex, ci)}>✕</button>
                  )}
                </div>
              </>
            )}
          </div>
        ))
      )}

      {/* Fallback coord display */}
      {step.fallbackCoord && (
        <div className="ci-row ci-row--coord">
          <span className="ci-badge ci-badge--none">coord</span>
          <span className="ci-value ci-value--coord">
            ({step.fallbackCoord[0]}, {step.fallbackCoord[1]})
          </span>
          <span className="ci-fallback-note">fallback if all candidates fail</span>
        </div>
      )}

      {/* Add new candidate */}
      {addingNew ? (
        <div className="ci-add-form">
          <select
            className="ci-edit-select"
            value={newStrategy}
            onChange={(e) => setNewStrategy(e.target.value as EditableStrategy)}
          >
            {STRATEGY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            className="ci-edit-input"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveNew(); if (e.key === "Escape") setAddingNew(false); }}
            autoFocus
            placeholder="value"
          />
          <button className="ci-btn ci-btn--save" onClick={saveNew}>Add</button>
          <button className="ci-btn" onClick={() => { setAddingNew(false); setNewValue(""); }}>Cancel</button>
        </div>
      ) : (
        <button className="ci-btn ci-btn--add" onClick={() => { setAddingNew(true); setEditingIndex(null); }}>
          + Add candidate
        </button>
      )}
    </div>
  );
}

/* ── StepEditForm — inline edit form for non-tap steps ───────────────────── */

interface StepEditFormProps {
  step: IRStep;
  onSave: (updated: IRStep) => void;
  onCancel: () => void;
}

export function StepEditForm({ step, onSave, onCancel }: StepEditFormProps) {
  const [pkg, setPkg]   = useState(() => (step.type === "launchApp" || step.type === "stopApp") ? step.packageName : "");
  const [durationMs, setDurationMs] = useState(() =>
    step.type === "wait" ? step.durationMs
    : step.type === "longPress" ? (step.durationMs ?? 800)
    : step.type === "swipe" ? (step.durationMs ?? 300)
    : step.type === "waitFor" ? (step.timeoutMs ?? 10000)
    : step.type === "scrollUntilVisible" ? (step.maxScrolls ?? 10)
    : 1000,
  );
  const [text, setText] = useState(() =>
    step.type === "typeText" ? step.text
    : step.type === "openUrl" ? step.url
    : step.type === "deepLink" ? step.url
    : "",
  );
  const [text2, setText2] = useState(() => step.type === "deepLink" ? (step.packageName ?? "") : "");
  const [key, setKey]   = useState<PressKeyName>(() => step.type === "pressKey" ? step.key : "back");
  const [label, setLabel] = useState(() => step.type === "screenshot" ? (step.label ?? "") : "");
  const [direction, setDirection] = useState<"up"|"down"|"left"|"right">(() =>
    (step.type === "swipe" || step.type === "scrollUntilVisible") ? (step.type === "scrollUntilVisible" ? (step.direction ?? "down") : "down") : "down",
  );
  const [waitState, setWaitState] = useState<"visible"|"hidden"|"enabled"|"disabled">(() =>
    step.type === "waitFor" ? (step.state ?? "visible") : "visible",
  );
  const [wifi, setWifi]         = useState<"on"|"off"|"unchanged">(() => step.type === "setDeviceState" ? (step.wifi === true ? "on" : step.wifi === false ? "off" : "unchanged") : "unchanged");
  const [airplane, setAirplane] = useState<"on"|"off"|"unchanged">(() => step.type === "setDeviceState" ? (step.airplane === true ? "on" : step.airplane === false ? "off" : "unchanged") : "unchanged");
  const [darkMode, setDarkMode] = useState<"on"|"off"|"unchanged">(() => step.type === "setDeviceState" ? (step.darkMode === true ? "on" : step.darkMode === false ? "off" : "unchanged") : "unchanged");
  const [location, setLocation] = useState<"on"|"off"|"unchanged">(() => step.type === "setDeviceState" ? (step.location === true ? "on" : step.location === false ? "off" : "unchanged") : "unchanged");
  // holdSwipe fields
  const [hsDirection, setHsDirection] = useState<"up"|"down"|"left"|"right">(() => step.type === "holdSwipe" ? (step.direction ?? "down") : "down");
  const [hsHoldMs, setHsHoldMs]       = useState(() => step.type === "holdSwipe" ? (step.holdMs ?? 800) : 800);
  const [hsDistance, setHsDistance]   = useState(() => step.type === "holdSwipe" ? (step.distance ?? 400) : 400);
  // assert fields
  const [assertionKind, setAssertionKind] = useState<AssertionKind>(() => step.type === "assert" ? step.assertion : "visible");
  const [assertValue, setAssertValue] = useState(() => step.type === "assert" ? (step.value ?? "") : "");
  // if fields
  const [ifAssertion, setIfAssertion] = useState<"visible"|"notVisible"|"enabled"|"disabled">(() => step.type === "if" ? step.condition.assertion : "visible");
  // locator selector for element-based steps
  const [locatorSelector, setLocatorSelector] = useState<Selector>(() => {
    if (step.type === "assert" || step.type === "clearText" || step.type === "doubleTap" ||
        step.type === "longPress" || step.type === "waitFor" || step.type === "scrollUntilVisible" ||
        step.type === "holdSwipe") {
      return step.selector;
    }
    if (step.type === "if") return step.condition.selector;
    return { description: "element", candidates: [] };
  });
  // repeat field
  const [repeatCount, setRepeatCount] = useState(() => step.type === "repeat" ? step.count : 3);
  // forEachRow fields
  const [dataSource, setDataSource] = useState(() => step.type === "forEachRow" ? step.dataSource : "");
  const [variable, setVariable]     = useState(() => step.type === "forEachRow" ? step.variable : "row");
  // print fields
  const [printMsg, setPrintMsg]   = useState(() => step.type === "print" ? step.message : "");
  const [printLevel, setPrintLevel] = useState<"info"|"warn"|"error">(() => step.type === "print" ? (step.level ?? "info") : "info");
  // setVariable fields
  const [svName, setSvName]     = useState(() => step.type === "setVariable" ? step.name : "");
  const [isSensitive, setIsSensitive] = useState(() => step.type === "typeText" ? (step.sensitive ?? false) : false);
  const [showUnmasked, setShowUnmasked] = useState(false);
  const [svKind, setSvKind]     = useState<"literal"|"capture"|"random">(() => step.type === "setVariable" ? step.source.kind : "literal");
  const [svLiteral, setSvLiteral] = useState(() => step.type === "setVariable" && step.source.kind === "literal" ? String(step.source.value) : "");
  const [svSelector, setSvSelector] = useState<Selector>(() =>
    step.type === "setVariable" && step.source.kind === "capture"
      ? step.source.selector
      : { description: "element", candidates: [] },
  );
  // evaluate fields
  const SEF_OPERATORS = ["==","!=",">","<",">=","<=","contains","notContains","startsWith","endsWith","matches","isEmpty","isNotEmpty"] as const;
  const [evLeft, setEvLeft]         = useState(() => step.type === "evaluate" ? step.left : "");
  const [evOp, setEvOp]             = useState(() => step.type === "evaluate" ? step.operator : "==");
  const [evRight, setEvRight]       = useState(() => step.type === "evaluate" ? (step.right ?? "") : "");
  const [evStoreAs, setEvStoreAs]   = useState(() => step.type === "evaluate" ? (step.storeAs ?? "") : "");
  const [evFail, setEvFail]         = useState(() => step.type === "evaluate" ? (step.failIfFalse ?? false) : false);
  const [evTrim, setEvTrim]         = useState(() => step.type === "evaluate" ? (step.options?.trim ?? false) : false);
  const [evIgnoreCase, setEvIgnoreCase] = useState(() => step.type === "evaluate" ? (step.options?.ignoreCase ?? false) : false);
  const [evParseNum, setEvParseNum] = useState(() => step.type === "evaluate" ? (step.options?.parseNumber ?? false) : false);

  function save() {
    switch (step.type) {
      case "launchApp": if (!pkg.trim()) return; onSave({ ...step, packageName: pkg.trim() }); break;
      case "stopApp":   if (!pkg.trim()) return; onSave({ ...step, packageName: pkg.trim() }); break;
      case "wait":      onSave({ ...step, durationMs: Math.max(1, durationMs) }); break;
      case "typeText":  onSave({ ...step, text, sensitive: isSensitive || undefined }); break;
      case "pressKey":  onSave({ ...step, key }); break;
      case "screenshot": onSave({ ...step, label: label.trim() || undefined }); break;
      case "longPress":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector, durationMs: Math.max(1, durationMs) }); break;
      case "swipe":     onSave({ ...step, durationMs: Math.max(1, durationMs) }); break;
      case "openUrl":   if (!text.trim()) return; onSave({ ...step, url: text.trim() }); break;
      case "deepLink":  if (!text.trim()) return; onSave({ ...step, url: text.trim(), ...(text2.trim() ? { packageName: text2.trim() } : { packageName: undefined }) }); break;
      case "waitFor":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector, state: waitState, timeoutMs: Math.max(1, durationMs) }); break;
      case "scrollUntilVisible":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector, direction, maxScrolls: Math.max(1, durationMs) }); break;
      case "setDeviceState": onSave({
        ...step,
        wifi:     wifi === "unchanged" ? undefined : wifi === "on",
        airplane: airplane === "unchanged" ? undefined : airplane === "on",
        darkMode: darkMode === "unchanged" ? undefined : darkMode === "on",
        location: location === "unchanged" ? undefined : location === "on",
      }); break;
      case "holdSwipe":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector, direction: hsDirection, holdMs: Math.max(100, hsHoldMs), distance: Math.max(50, hsDistance) });
        break;
      case "doubleTap":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector }); break;
      case "clearText":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector }); break;
      case "assert":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, selector: locatorSelector, assertion: assertionKind, value: assertionKind === "hasText" ? assertValue : undefined }); break;
      case "if":
        if (locatorSelector.candidates.length === 0) return;
        onSave({ ...step, condition: { selector: locatorSelector, assertion: ifAssertion } }); break;
      case "repeat": onSave({ ...step, count: Math.max(1, repeatCount) }); break;
      case "forEachRow": if (!dataSource.trim() || !variable.trim()) return; onSave({ ...step, dataSource: dataSource.trim(), variable: variable.trim() }); break;
      case "print": if (!printMsg.trim()) return; onSave({ ...step, message: printMsg, level: printLevel }); break;
      case "checkpoint": if (!printMsg.trim()) return; onSave({ ...step, message: printMsg, status: (printLevel === "info" ? "pass" : "fail") as "pass"|"fail" }); break;
      case "stopTest": onSave({ ...step, message: printMsg || "Test stopped", status: (printLevel === "info" ? "passed" : "failed") as "passed"|"failed" }); break;
      case "break": onSave(step); break;
      case "continue": onSave(step); break;
      case "evaluate": {
        if (!evLeft.trim()) return;
        const needsR = !["isEmpty","isNotEmpty"].includes(evOp);
        const opts = (evTrim || evIgnoreCase || evParseNum) ? { trim: evTrim||undefined, ignoreCase: evIgnoreCase||undefined, parseNumber: evParseNum||undefined } : undefined;
        onSave({ ...step, left: evLeft.trim(), operator: evOp as import("@mimiq/ir").CompareOperator, right: (needsR && evRight.trim()) ? evRight.trim() : undefined, options: opts, storeAs: evStoreAs.trim()||undefined, failIfFalse: evFail||undefined } as import("@mimiq/ir").EvaluateStep);
        break;
      }
      case "setVariable": {
        if (!svName.trim()) return;
        if (svKind === "literal") {
          onSave({ ...step, name: svName.trim(), source: { kind: "literal", value: svLiteral } } as SetVariableStep);
        } else if (svKind === "random") {
          // Preserve existing random config — user edits the whole step via the toolbar
          onSave({ ...step, name: svName.trim() } as SetVariableStep);
        } else {
          if (svSelector.candidates.length === 0) return;
          onSave({ ...step, name: svName.trim(), source: { kind: "capture", selector: svSelector } } as SetVariableStep);
        }
        break;
      }
      default: onCancel();
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") onCancel();
  }

  return (
    <div className="step-edit-form">
      {(step.type === "launchApp" || step.type === "stopApp") && (
        <div className="sef-row">
          <label className="sef-label">Package:</label>
          <input className="sef-input" value={pkg} onChange={(e) => setPkg(e.target.value)} onKeyDown={onKeyDown} autoFocus />
        </div>
      )}
      {step.type === "wait" && (
        <div className="sef-row">
          <label className="sef-label">Duration:</label>
          <input type="number" className="sef-input sef-input--short" min="1" value={durationMs} onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} onKeyDown={onKeyDown} autoFocus />
          <span className="sef-unit">ms</span>
        </div>
      )}
      {step.type === "longPress" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Hold (ms):</label>
            <input type="number" className="sef-input sef-input--short" min="1" value={durationMs} onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} onKeyDown={onKeyDown} />
            <span className="sef-unit">ms</span>
          </div>
        </>
      )}
      {step.type === "typeText" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Text:</label>
            <input
              className="sef-input"
              type={isSensitive && !showUnmasked ? "password" : "text"}
              value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown} autoFocus
              placeholder={isSensitive ? "•••••••• (password hidden)" : undefined}
            />
            {isSensitive && (
              <button
                type="button" className="sef-unmask-btn"
                onClick={() => setShowUnmasked((v) => !v)}
                title={showUnmasked ? "Hide password" : "Reveal password to edit"}
              >
                {showUnmasked ? "🙈 Hide" : "👁 Unmask"}
              </button>
            )}
          </div>
          <div className="sef-row" style={{ marginTop: 2 }}>
            <label className="sef-label"> </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: "0.78rem", color: isSensitive ? "var(--color-warning)" : "var(--color-text-muted)" }}>
              <input type="checkbox" checked={isSensitive} onChange={(e) => { setIsSensitive(e.target.checked); if (!e.target.checked) setShowUnmasked(false); }} />
              🔒 Password / sensitive — mask in all reports and logs
            </label>
          </div>
          {isSensitive && (
            <p className="sef-info" style={{ color: "var(--color-warning)", paddingLeft: "6.5rem" }}>
              Value is stored in the test file. Masking only hides it from display. Do not use for production credentials.
            </p>
          )}
        </>
      )}
      {step.type === "pressKey" && (
        <div className="sef-row">
          <label className="sef-label">Key:</label>
          <select className="sef-select" value={key} onChange={(e) => setKey(e.target.value as PressKeyName)} autoFocus>
            {(["back","home","enter","tab","delete"] as PressKeyName[]).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      )}
      {step.type === "screenshot" && (
        <div className="sef-row">
          <label className="sef-label">Label:</label>
          <input className="sef-input" placeholder="optional label" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={onKeyDown} autoFocus />
        </div>
      )}
      {(step.type === "openUrl" || step.type === "deepLink") && (
        <div className="sef-row">
          <label className="sef-label">URL:</label>
          <input className="sef-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} autoFocus />
        </div>
      )}
      {step.type === "deepLink" && (
        <div className="sef-row">
          <label className="sef-label">Package:</label>
          <input className="sef-input" placeholder="optional" value={text2} onChange={(e) => setText2(e.target.value)} onKeyDown={onKeyDown} />
        </div>
      )}
      {step.type === "swipe" && (
        <div className="sef-row">
          <label className="sef-label">Duration:</label>
          <input type="number" className="sef-input sef-input--short" min="1" value={durationMs} onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} onKeyDown={onKeyDown} autoFocus />
          <span className="sef-unit">ms</span>
        </div>
      )}
      {step.type === "scrollUntilVisible" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Direction:</label>
            <select className="sef-select" value={direction} onChange={(e) => setDirection(e.target.value as typeof direction)}>
              {(["down","up","left","right"] as const).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Max scrolls:</label>
            <input type="number" className="sef-input sef-input--short" min="1" max="50" value={durationMs} onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} onKeyDown={onKeyDown} />
          </div>
        </>
      )}
      {step.type === "waitFor" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Wait for:</label>
            <select className="sef-select" value={waitState} onChange={(e) => setWaitState(e.target.value as typeof waitState)}>
              {(["visible","hidden","enabled","disabled"] as const).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Timeout:</label>
            <input type="number" className="sef-input sef-input--short" min="1" value={durationMs} onChange={(e) => setDurationMs(parseInt(e.target.value) || 1)} onKeyDown={onKeyDown} />
            <span className="sef-unit">ms</span>
          </div>
        </>
      )}
      {step.type === "setDeviceState" && (
        <>
          <div className="sef-row">
            <label className="sef-label">WiFi:</label>
            <select className="sef-select" value={wifi} onChange={(e) => setWifi(e.target.value as typeof wifi)} autoFocus>
              <option value="unchanged">No change</option>
              <option value="on">Turn ON</option>
              <option value="off">Turn OFF</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Airplane:</label>
            <select className="sef-select" value={airplane} onChange={(e) => setAirplane(e.target.value as typeof airplane)}>
              <option value="unchanged">No change</option>
              <option value="on">Turn ON</option>
              <option value="off">Turn OFF</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Dark Mode:</label>
            <select className="sef-select" value={darkMode} onChange={(e) => setDarkMode(e.target.value as typeof darkMode)}>
              <option value="unchanged">No change</option>
              <option value="on">Turn ON</option>
              <option value="off">Turn OFF</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Location:</label>
            <select className="sef-select" value={location} onChange={(e) => setLocation(e.target.value as typeof location)}>
              <option value="unchanged">No change</option>
              <option value="on">Turn ON</option>
              <option value="off">Turn OFF</option>
            </select>
          </div>
          {step.showKeyboard && <p className="sef-info">Show Keyboard — fires a one-shot keyevent. No settings to change.</p>}
        </>
      )}
      {/* holdSwipe */}
      {/* evaluate */}
      {step.type === "evaluate" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Left:</label>
            <input className="sef-input" placeholder='${myVar} or "text"' value={evLeft} onChange={(e) => setEvLeft(e.target.value)} onKeyDown={onKeyDown} autoFocus />
          </div>
          <div className="sef-row">
            <label className="sef-label">Operator:</label>
            <select className="sef-select" value={evOp} onChange={(e) => setEvOp(e.target.value)}>
              {SEF_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
          {!["isEmpty","isNotEmpty"].includes(evOp) && (
            <div className="sef-row">
              <label className="sef-label">Right:</label>
              <input className="sef-input" placeholder='${other} or "value"' value={evRight} onChange={(e) => setEvRight(e.target.value)} onKeyDown={onKeyDown} />
            </div>
          )}
          <div className="sef-row">
            <label className="sef-label">Store in:</label>
            <input className="sef-input sef-input--short" placeholder="resultVar (optional)" value={evStoreAs} onChange={(e) => setEvStoreAs(e.target.value)} onKeyDown={onKeyDown} />
          </div>
          <div className="sef-row" style={{ flexWrap:"wrap", gap:"0.75rem" }}>
            <label style={{ display:"flex", gap:4, alignItems:"center", fontSize:"0.8rem", cursor:"pointer" }}><input type="checkbox" checked={evFail} onChange={(e) => setEvFail(e.target.checked)} /> Fail step if false</label>
            <label style={{ display:"flex", gap:4, alignItems:"center", fontSize:"0.8rem", cursor:"pointer" }}><input type="checkbox" checked={evTrim} onChange={(e) => setEvTrim(e.target.checked)} /> Trim</label>
            <label style={{ display:"flex", gap:4, alignItems:"center", fontSize:"0.8rem", cursor:"pointer" }}><input type="checkbox" checked={evIgnoreCase} onChange={(e) => setEvIgnoreCase(e.target.checked)} /> Ignore case</label>
            <label style={{ display:"flex", gap:4, alignItems:"center", fontSize:"0.8rem", cursor:"pointer" }}><input type="checkbox" checked={evParseNum} onChange={(e) => setEvParseNum(e.target.checked)} /> Parse as number</label>
          </div>
        </>
      )}
      {step.type === "holdSwipe" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Direction:</label>
            <select className="sef-select" value={hsDirection} onChange={(e) => setHsDirection(e.target.value as typeof hsDirection)}>
              {(["down","up","left","right"] as const).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Hold (ms):</label>
            <input type="number" className="sef-input sef-input--short" min="100" value={hsHoldMs}
              onChange={(e) => setHsHoldMs(parseInt(e.target.value) || 800)} onKeyDown={onKeyDown} />
            <span className="sef-unit">ms</span>
          </div>
          <div className="sef-row">
            <label className="sef-label">Distance (px):</label>
            <input type="number" className="sef-input sef-input--short" min="50" value={hsDistance}
              onChange={(e) => setHsDistance(parseInt(e.target.value) || 400)} onKeyDown={onKeyDown} />
            <span className="sef-unit">px</span>
          </div>
        </>
      )}
      {/* hideKeyboard — no locator needed */}
      {step.type === "hideKeyboard" && (
        <p className="sef-info">Dismisses the on-screen keyboard. No element selection needed.</p>
      )}
      {/* doubleTap / clearText — need a locator */}
      {(step.type === "doubleTap" || step.type === "clearText") && (
        <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
      )}
      {/* assert */}
      {step.type === "assert" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Condition:</label>
            <select className="sef-select" value={assertionKind} onChange={(e) => setAssertionKind(e.target.value as AssertionKind)}>
              <option value="visible">is visible</option>
              <option value="notVisible">is not visible</option>
              <option value="enabled">is enabled</option>
              <option value="disabled">is disabled</option>
              <option value="checked">is checked</option>
              <option value="unchecked">is unchecked</option>
              <option value="hasText">has text</option>
            </select>
          </div>
          {assertionKind === "hasText" && (
            <div className="sef-row">
              <label className="sef-label">Expected text:</label>
              <input className="sef-input" value={assertValue} onChange={(e) => setAssertValue(e.target.value)} placeholder="exact text to match" />
            </div>
          )}
        </>
      )}
      {/* if condition */}
      {step.type === "if" && (
        <>
          <LocatorField selector={locatorSelector} onChange={setLocatorSelector} autoFocus showLabel />
          <div className="sef-row">
            <label className="sef-label">Condition:</label>
            <select className="sef-select" value={ifAssertion} onChange={(e) => setIfAssertion(e.target.value as typeof ifAssertion)}>
              <option value="visible">is visible</option>
              <option value="notVisible">is not visible</option>
              <option value="enabled">is enabled</option>
              <option value="disabled">is disabled</option>
            </select>
          </div>
          <p className="sef-info">Then: {step.then.length} step{step.then.length !== 1 ? "s" : ""}
            {step.else && step.else.length > 0 ? ` · Else: ${step.else.length} step${step.else.length !== 1 ? "s" : ""}` : ""}
            {" "}(nested steps editable in Phase 3.5)</p>
        </>
      )}
      {/* repeat */}
      {step.type === "repeat" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Repeat count:</label>
            <input type="number" className="sef-input sef-input--short" min="1" max="1000" value={repeatCount} onChange={(e) => setRepeatCount(parseInt(e.target.value) || 1)} autoFocus />
            <span className="sef-unit">times</span>
          </div>
          <p className="sef-info">Contains {step.steps.length} step{step.steps.length !== 1 ? "s" : ""} (nested editing in Phase 3.5)</p>
        </>
      )}
      {/* forEachRow */}
      {step.type === "forEachRow" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Data source:</label>
            <input className="sef-input" value={dataSource} onChange={(e) => setDataSource(e.target.value)} placeholder="e.g. users, testData" autoFocus />
          </div>
          <div className="sef-row">
            <label className="sef-label">Row variable:</label>
            <input className="sef-input" value={variable} onChange={(e) => setVariable(e.target.value)} placeholder="e.g. row, item" />
          </div>
          <p className="sef-info">Contains {step.steps.length} step{step.steps.length !== 1 ? "s" : ""} per row (nested editing in Phase 3.5)</p>
        </>
      )}
      {/* checkpoint (Print Pass / Print Fail) */}
      {step.type === "checkpoint" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Result:</label>
            <select className="sef-select" value={printLevel === "info" ? "pass" : "fail"} onChange={(e) => setPrintLevel(e.target.value === "pass" ? "info" : "error")} autoFocus>
              <option value="pass">✓ PASS</option>
              <option value="fail">✗ FAIL</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Message:</label>
            <input className="sef-input" value={printMsg} onChange={(e) => setPrintMsg(e.target.value)} placeholder="e.g. Payment completed successfully" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") onCancel(); }} />
          </div>
          <p className="sef-info">This verdict appears in the report. FAIL marks the step as failed and fails the test.</p>
        </>
      )}
      {/* stopTest */}
      {step.type === "stopTest" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Stop as:</label>
            <select className="sef-select" value={printLevel === "info" ? "passed" : "failed"} onChange={(e) => setPrintLevel(e.target.value === "passed" ? "info" : "error")} autoFocus>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Message:</label>
            <input className="sef-input" value={printMsg} onChange={(e) => setPrintMsg(e.target.value)} placeholder="e.g. Test aborted: user not authenticated" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") onCancel(); }} />
          </div>
          <p className="sef-info">Halts execution immediately. No further steps will run.</p>
        </>
      )}
      {/* break / continue */}
      {(step.type === "break" || step.type === "continue") && (
        <p className="sef-info">{step.type === "break" ? "Exits the current loop immediately. Use inside Repeat or For Each Row." : "Skips the remaining steps in the current iteration and moves to the next row/count. Use inside Repeat or For Each Row."}</p>
      )}
      {/* print */}
      {step.type === "print" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Level:</label>
            <select className="sef-select" value={printLevel} onChange={(e) => setPrintLevel(e.target.value as typeof printLevel)} autoFocus>
              <option value="info">ℹ info</option>
              <option value="warn">⚠ warn</option>
              <option value="error">✕ error</option>
            </select>
          </div>
          <div className="sef-row">
            <label className="sef-label">Message:</label>
            <input
              className="sef-input"
              value={printMsg}
              onChange={(e) => setPrintMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") onCancel(); }}
              placeholder={`Hello \${username}, env: \${env:BASE_URL}`}
            />
          </div>
          <p className="sef-info">Use <code>{"${variableName}"}</code> to embed values · <code>{"\\${"}</code> for a literal $&#123;</p>
        </>
      )}
      {/* setVariable */}
      {step.type === "setVariable" && (
        <>
          <div className="sef-row">
            <label className="sef-label">Variable name:</label>
            <input
              className="sef-input"
              value={svName}
              onChange={(e) => setSvName(e.target.value)}
              placeholder="e.g. loginCode, totalPrice"
              autoFocus
            />
          </div>
          <p className="sef-info" style={{ paddingLeft: "6.5rem" }}>Use as <code>{"${" + (svName.trim() || "varName") + "}"}</code> in later steps.</p>
          <div className="sef-row">
            <label className="sef-label">Set from:</label>
            <select className="sef-select" value={svKind} onChange={(e) => setSvKind(e.target.value as "literal" | "capture" | "random")}
              disabled={svKind === "random"}>
              <option value="literal">Fixed value</option>
              <option value="capture">Capture from screen</option>
              {svKind === "random" && <option value="random">Random value</option>}
            </select>
          </div>
          {svKind === "literal" && (
            <div className="sef-row">
              <label className="sef-label">Value:</label>
              <input
                className="sef-input"
                value={svLiteral}
                onChange={(e) => setSvLiteral(e.target.value)}
                placeholder='e.g. 42, "hello", true'
              />
            </div>
          )}
          {svKind === "capture" && (
            <>
              <p className="sef-info">Reads the visible text of the matched element at replay time.</p>
              <LocatorField selector={svSelector} onChange={setSvSelector} showLabel={false} />
            </>
          )}
          {svKind === "random" && step.type === "setVariable" && step.source.kind === "random" && (
            <p className="sef-info">
              Generates a random <strong>{step.source.randomType}</strong> value
              {step.source.randomType !== "number"
                ? ` of length ${step.source.length ?? 8}`
                : ` between ${step.source.min ?? 0} and ${step.source.max ?? 999999}`} each run.
              To change the random settings, delete this step and add a new Random Value command.
            </p>
          )}
        </>
      )}
      <div className="sef-actions">
        <button className="ci-btn ci-btn--save" onClick={save}>Save</button>
        <button className="ci-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── ReplayPanel ──────────────────────────────────────────────────────────── */

interface ReplayPanelProps {
  adb: Adb;
  steps: readonly IRStep[];
  replaying: boolean;
  results: ReplayStepResult[] | null;
  executingIndex: number | null;
  error: string | null;
  stepDelayMs: number;
  onChangeDelay: (ms: number) => void;
  onReplay: (steps: readonly IRStep[], adb: Adb) => void;
  onStopReplay: () => void;
}

function ReplayPanel({ adb, steps, replaying, results, executingIndex, error, stepDelayMs, onChangeDelay, onReplay, onStopReplay }: ReplayPanelProps) {
  const okCount = results?.filter((r) => r.status === "ok").length ?? 0;
  const failCount = results?.filter((r) => r.status !== "ok").length ?? 0;
  const hasProgress = results !== null;

  return (
    <section className="replay-panel">
      <div className="replay-header">
        <strong>Replay</strong>
        {hasProgress && (
          <span className="replay-summary">
            {okCount} ok{failCount > 0 ? ` · ${failCount} failed` : ""}
          </span>
        )}
        {replaying ? (
          <>
            <span className="replay-running-label"><span className="btn-spinner" /> Replaying…</span>
            <button className="btn btn--danger replay-btn" onClick={onStopReplay} title="Stop the replay immediately">
              ■ Stop
            </button>
          </>
        ) : (
          <button
            className="btn btn--primary replay-btn"
            disabled={steps.length === 0}
            onClick={() => onReplay(steps, adb)}
          >
            {`▶ Replay ${steps.length} step${steps.length !== 1 ? "s" : ""}`}
          </button>
        )}
      </div>

      <div className="replay-delay-row">
        <label className="replay-delay-label">Delay between steps:</label>
        <input
          type="number"
          className="replay-delay-input"
          min="0"
          max="10000"
          step="100"
          value={stepDelayMs}
          onChange={(e) => onChangeDelay(Math.max(0, parseInt(e.target.value) || 0))}
        />
        <span className="replay-delay-unit">ms</span>
      </div>

      {error && <p className="replay-error">{error}</p>}

      {/* Live progress log — visible from the moment replay starts */}
      {hasProgress && (
        <ol className="replay-results">
          {/* Completed steps */}
          {results!.map((r) => (
            <li
              key={r.stepId}
              className={`replay-result-item replay-result-item--${r.status}`}
            >
              <span className="step-num">{r.stepIndex + 1}</span>
              <span className={`replay-badge replay-badge--${r.status}`}>
                {r.status === "ok" ? "OK" : r.status === "not_found" ? "NOT FOUND" : "ERROR"}
              </span>
              <span className="step-type">{steps[r.stepIndex]?.type}</span>
              <span className="replay-step-name">{steps[r.stepIndex] ? stepDescription(steps[r.stepIndex]!) : ""}</span>
              {r.status === "ok" && r.x !== undefined && (
                <span className="replay-coords">({r.x}, {r.y})</span>
              )}
              {r.status === "ok" && r.matchedBy && (
                <span className={`replay-matchedby replay-matchedby--${strategyConfidence(r.matchedBy)}`}>
                  {r.matchedBy}
                </span>
              )}
              {r.status === "ok" && r.detail && (
                <span className="replay-detail">{r.detail}</span>
              )}
              {r.status !== "ok" && r.detail && (
                <span className="replay-detail replay-detail--fail">{r.detail}</span>
              )}
              {r.status === "not_found" && r.healHint && (
                <span className="replay-heal-hint">{r.healHint}</span>
              )}
            </li>
          ))}

          {/* Currently executing step */}
          {executingIndex !== null && steps[executingIndex] && (
            <li key="__executing" className="replay-result-item replay-result-item--executing">
              <span className="step-num">{executingIndex + 1}</span>
              <span className="replay-badge replay-badge--executing">
                <span className="replay-spinner" /> running
              </span>
              <span className="step-type">{steps[executingIndex]!.type}</span>
              <span className="replay-step-name">{stepDescription(steps[executingIndex]!)}</span>
            </li>
          )}
        </ol>
      )}
    </section>
  );
}

/* ── DeviceMirror ─────────────────────────────────────────────────────────── */

interface ClickRipple {
  id: number;
  x: number; // CSS px relative to canvas top-left
  y: number;
}

interface InspectHighlight {
  bounds: import("./device/treeParser.js").A11yBounds;
  deviceW: number;
  deviceH: number;
}

interface DeviceMirrorProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  mirrorState: MirrorState;
  onStop: (() => void) | undefined;
  onRestart: (() => void) | undefined;
  /** When set, renders a cyan highlight rect over the inspected element. */
  inspectHighlight?: InspectHighlight;
  /** When set, fires a ripple at the given device coordinates (for execution overlay). */
  externalRipple?: { deviceX: number; deviceY: number; seq: number } | null;
}

function DeviceMirror({ canvasRef, mirrorState, onStop, onRestart, inspectHighlight, externalRipple }: DeviceMirrorProps) {
  const label = mirrorLabel(mirrorState.status);
  const isActive = mirrorState.status === "streaming";
  const hasError = mirrorState.status === "error";
  const isLandscape = isActive && mirrorState.width > mirrorState.height;

  // Click-indicator overlay settings
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayColor, setOverlayColor] = useState("#ff4444");
  const [ripples, setRipples] = useState<ClickRipple[]>([]);
  const rippleCounter = useRef(0);
  const overlayId = useId();

  function handleCanvasPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!overlayEnabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = ++rippleCounter.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 600);
  }

  // Fire a ripple from device coordinates (execution overlay)
  useEffect(() => {
    if (!externalRipple || !canvasRef.current || mirrorState.status !== "streaming") return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width  / mirrorState.width;
    const scaleY = rect.height / mirrorState.height;
    const cssX = externalRipple.deviceX * scaleX;
    const cssY = externalRipple.deviceY * scaleY;
    const id = ++rippleCounter.current;
    setRipples((prev) => [...prev, { id, x: cssX, y: cssY }]);
    setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalRipple?.seq]);

  return (
    <section className="mirror-section">
      <div className="mirror-header">
        <span className="mirror-label">{label}</span>
        {mirrorState.status === "streaming" && (
          <span className="mirror-dims">
            {mirrorState.width}×{mirrorState.height}
          </span>
        )}
        {mirrorState.status === "streaming" && mirrorState.width > mirrorState.height && (
          <span className="mirror-landscape-badge" title="Device is in landscape orientation — recorder panel is automatically resized">
            🌐 Landscape
          </span>
        )}
        {mirrorState.status === "streaming" && (
          <span className="mirror-frames">
            {mirrorState.framesRendered > 0
              ? `▶ ${mirrorState.framesRendered} frames`
              : "⏳ waiting for first frame…"}
          </span>
        )}
        {isActive && (
          <button className="btn btn--danger mirror-stop" onClick={onStop}>
            Stop Mirror
          </button>
        )}
        {(mirrorState.status === "stopped" || mirrorState.status === "idle") && onRestart && (
          <button className="btn btn--primary mirror-stop" onClick={onRestart}>
            ▶ Start Mirror
          </button>
        )}
      </div>

      {/* Click-indicator settings — only shown when streaming */}
      {isActive && (
        <div className="mirror-overlay-settings">
          <label className="mos-label" htmlFor={`${overlayId}-toggle`}>
            Click indicator
          </label>
          <input
            id={`${overlayId}-toggle`}
            type="checkbox"
            className="mos-checkbox"
            checked={overlayEnabled}
            onChange={(e) => setOverlayEnabled(e.target.checked)}
          />
          <input
            type="color"
            className="mos-color"
            value={overlayColor}
            disabled={!overlayEnabled}
            title="Indicator colour"
            onChange={(e) => setOverlayColor(e.target.value)}
          />
        </div>
      )}

      {hasError && (
        <p className="mirror-error">
          Mirror error: {mirrorState.status === "error" ? mirrorState.message : ""}
        </p>
      )}

      {/* Canvas + ripple overlay wrapper.
          When rotated: canvas is CSS-rotated 90° so a landscape app appears upright.
          The wrapper gets aspect-ratio swapped so it reserves the correct visual space. */}
      <div className="mirror-canvas-wrapper">
        <canvas
          ref={canvasRef}
          className={`mirror-canvas ${isActive ? "mirror-canvas--active" : ""}`}
          style={
            mirrorState.status === "streaming" && mirrorState.width > 0
              ? { aspectRatio: `${mirrorState.width} / ${mirrorState.height}` }
              : undefined
          }
          onPointerDown={handleCanvasPointerDown}
        />
        {/* Pointer-events:none overlay: ripples + inspect highlight */}
        <div className="mirror-overlay" aria-hidden="true">
          {ripples.map((r) => (
            <div
              key={r.id}
              className="click-ripple"
              style={{ left: r.x, top: r.y, "--ripple-color": overlayColor } as React.CSSProperties}
            />
          ))}
          {/* Inspect highlight — cyan rect over the selected element */}
          {inspectHighlight && inspectHighlight.deviceW > 0 && (
            <InspectHighlightRect
              bounds={inspectHighlight.bounds}
              deviceW={inspectHighlight.deviceW}
              deviceH={inspectHighlight.deviceH}
            />
          )}
        </div>
      </div>
    </section>
  );
}

/* ── InspectHighlightRect ─────────────────────────────────────────────────── */

function InspectHighlightRect({
  bounds, deviceW, deviceH,
}: {
  bounds: import("./device/treeParser.js").A11yBounds;
  deviceW: number;
  deviceH: number;
}) {
  // The mirror-overlay is 100%×100% over the canvas.
  // We convert device-pixel bounds → percentage positions so the rect
  // is correct regardless of the CSS display size.
  const left   = (bounds.left   / deviceW) * 100;
  const top    = (bounds.top    / deviceH) * 100;
  const width  = ((bounds.right  - bounds.left) / deviceW) * 100;
  const height = ((bounds.bottom - bounds.top)  / deviceH) * 100;

  return (
    <div
      className="inspect-highlight-rect"
      style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
    />
  );
}

function mirrorLabel(status: MirrorState["status"]): string {
  switch (status) {
    case "idle": return "Mirror: Ready";
    case "pushing": return "Mirror: Preparing…";
    case "starting": return "Mirror: Starting…";
    case "streaming": return "Mirror: Streaming";
    case "error": return "Mirror: Error";
    case "stopped": return "Mirror: Stopped";
  }
}

/* ── TreePanel ────────────────────────────────────────────────────────────── */

interface TreePanelProps {
  adb: Adb;
  nodes: A11yNode[] | null;
  loading: boolean;
  error: string | null;
  onDump: (adb: Adb) => void;
}

function TreePanel({ adb, nodes, loading, error, onDump }: TreePanelProps) {
  const nodeCount = nodes ? countNodes(nodes) : null;

  return (
    <section className="tree-panel">
      <div className="tree-header">
        <strong>UI Elements</strong>
        <button
          className="btn btn--primary"
          disabled={loading}
          onClick={() => onDump(adb)}
        >
          {loading ? "Scanning…" : "Scan UI"}
        </button>
      </div>
      {error && <p className="tree-error">{error}</p>}
      {nodeCount !== null && (
        <p className="tree-count">{nodeCount} node{nodeCount !== 1 ? "s" : ""} parsed</p>
      )}
      {nodes && nodes.length > 0 && (
        <pre className="tree-json">
          {JSON.stringify(nodes.slice(0, 2), null, 2)}
          {nodes.length > 2 ? "\n…" : ""}
        </pre>
      )}
    </section>
  );
}

function countNodes(nodes: A11yNode[]): number {
  let n = 0;
  function walk(list: A11yNode[]) {
    for (const node of list) {
      n++;
      walk(node.children);
    }
  }
  walk(nodes);
  return n;
}

/* ── ConnectButton ────────────────────────────────────────────────────────── */

interface ConnectButtonProps {
  state: DeviceState;
  onConnect: () => void;
  onDisconnect: () => void;
}

function ConnectButton({ state, onConnect, onDisconnect }: ConnectButtonProps) {
  const isConnecting =
    state.status === "requesting" ||
    state.status === "connecting" ||
    state.status === "authenticating";

  const label =
    state.status === "connected"
      ? "Disconnect"
      : isConnecting
        ? connectingLabel(state.status)
        : "Connect Phone";

  const variant =
    state.status === "connected"
      ? "btn--danger"
      : state.status === "error"
        ? "btn--warning"
        : "btn--primary";

  return (
    <button
      className={`btn ${variant}`}
      disabled={isConnecting}
      onClick={state.status === "connected" ? onDisconnect : onConnect}
    >
      {isConnecting && <span className="btn-spinner" />}
      {label}
    </button>
  );
}

function connectingLabel(status: DeviceState["status"]): string {
  if (status === "requesting") return "Select device…";
  if (status === "connecting") return "Connecting…";
  if (status === "authenticating") return "Authorising…";
  return "Connecting…";
}

/* ── ConnectionDoctorPanel ────────────────────────────────────────────────── */

interface ConnectionDoctorPanelProps {
  error: ConnectionError;
  onRetry: () => void;
}

function ConnectionDoctorPanel({ error, onRetry }: ConnectionDoctorPanelProps) {
  return (
    <div className="doctor-panel">
      <div className="doctor-header">
        <span className="doctor-icon">⚠</span>
        <strong>Connection Doctor</strong>
        <span className="doctor-kind">{error.kind}</span>
      </div>
      <p className="doctor-message">{error.message}</p>
      <ol className="doctor-steps">
        {error.steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      <button className="btn btn--primary doctor-retry" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/* ── CapabilityCard ────────────────────────────────────────────────────────── */

interface CapabilityCardProps {
  label: string;
  ok: boolean;
  okText: string;
  failText: string;
}

function CapabilityCard({ label, ok, okText, failText }: CapabilityCardProps) {
  return (
    <div className={`cap-card ${ok ? "cap-ok" : "cap-fail"}`}>
      <span className="cap-icon">{ok ? "✓" : "✗"}</span>
      <strong>{label}</strong>
      <p>{ok ? okText : failText}</p>
    </div>
  );
}
